import { AIProvider, APIError, generateUUID, isJSONObject, StreamingError, providerDisplayName, isOpenAIReasoningEffort, OpenAIResponsesRouting, isMuseReasoningEffort, StreamingEvents, AIProviderAccessPolicy, OpenAISettings, VeniceSettings, MiniMaxSettings, ZaiSettings, PerplexitySettings, GrokReasoningSettings, GeminiSettings, AnthropicSettings, createMessage, ErrorResponseParser, providerBaseURL, providerSupportsStreaming, OpenAICompatibleSettings, createStreamingConfig, streamWithHandler, partialToToolCall, isAIProvider, createPartialToolCall, tokenUsage } from './chunk-3PUWXRLZ.js';

// src/client/http/fetch-transport.ts
var defaultTransport = async (request) => {
  try {
    return await fetch(request.url, {
      method: request.method,
      headers: { ...request.headers },
      // Uint8Array bodies satisfy BodyInit in all supported runtimes.
      body: request.body ?? null,
      signal: request.signal ?? null
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw APIError.cancelled();
    }
    throw APIError.networkError(error instanceof Error ? error.message : String(error));
  }
};

// src/client/oauth/oauth-credential.ts
function credentialFromTokenResponse(init) {
  return {
    provider: init.provider,
    accessToken: init.accessToken,
    refreshToken: init.refreshToken,
    expiresAt: new Date(Date.now() + init.expiresIn * 1e3),
    email: init.email,
    accountId: init.accountId
  };
}
function credentialNeedsRefresh(credential) {
  return Date.now() >= credential.expiresAt.getTime() - 3e5;
}
function credentialIsExpired(credential) {
  return Date.now() >= credential.expiresAt.getTime();
}
function credentialToJSON(credential) {
  const json = {
    provider: credential.provider,
    accessToken: credential.accessToken,
    refreshToken: credential.refreshToken,
    expiresAt: credential.expiresAt.toISOString()
  };
  if (credential.email !== void 0) json.email = credential.email;
  if (credential.accountId !== void 0) json.accountId = credential.accountId;
  return json;
}
function credentialFromJSON(json) {
  return {
    provider: json.provider,
    accessToken: json.accessToken,
    refreshToken: json.refreshToken,
    expiresAt: new Date(json.expiresAt),
    email: json.email,
    accountId: json.accountId
  };
}
var InMemoryCredentialStore = class {
  credentials = /* @__PURE__ */ new Map();
  async save(credential) {
    this.credentials.set(credential.provider, credential);
  }
  async load(provider) {
    return this.credentials.get(provider) ?? null;
  }
  async delete(provider) {
    this.credentials.delete(provider);
  }
  async hasCredential(provider) {
    return this.credentials.has(provider);
  }
  async storedProviders() {
    return [...this.credentials.keys()];
  }
  async clearAll() {
    this.credentials.clear();
  }
};
var OAuthError = class _OAuthError extends Error {
  kind;
  constructor(kind, message) {
    super(message);
    this.name = "OAuthError";
    this.kind = kind;
  }
  static userCancelled() {
    return new _OAuthError("userCancelled", "Authentication was cancelled");
  }
  static authenticationFailed(message) {
    return new _OAuthError("authenticationFailed", `Authentication failed: ${message}`);
  }
  static refreshFailed(message) {
    return new _OAuthError("refreshFailed", `Token refresh failed: ${message}`);
  }
  static noCredential() {
    return new _OAuthError("noCredential", "No OAuth credential available");
  }
  static credentialExpired() {
    return new _OAuthError("credentialExpired", "OAuth credential has expired");
  }
  static storeError(message) {
    return new _OAuthError("storeError", `Credential store error: ${message}`);
  }
  static networkError(message) {
    return new _OAuthError("networkError", `Network error: ${message}`);
  }
  static invalidResponse(message) {
    return new _OAuthError("invalidResponse", `Invalid response: ${message}`);
  }
  static unsupportedProvider(provider) {
    return new _OAuthError("unsupportedProvider", `Provider '${provider}' doesn't support OAuth`);
  }
};

// src/client/oauth/openai-codex-oauth.ts
var OPENAI_CODEX_PROVIDER = "openai-codex";
var CLIENT_ID = "app_EMoamEEZ73f0CkXaXp7hrann";
var AUTHORIZATION_ENDPOINT = "https://auth.openai.com/oauth/authorize";
var TOKEN_ENDPOINT = "https://auth.openai.com/oauth/token";
var OPENAI_CODEX_CALLBACK_SCHEME = "axonproviderkit";
var OPENAI_CODEX_CALLBACK_URL = `${OPENAI_CODEX_CALLBACK_SCHEME}://oauth-callback`;
function generateCodeVerifier() {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}
async function computeCodeChallenge(verifier) {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return base64UrlEncode(new Uint8Array(digest));
}
function base64UrlEncode(bytes) {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
var OpenAICodexOAuth = class {
  static provider = OPENAI_CODEX_PROVIDER;
  credentialStore;
  transport;
  constructor(options = {}) {
    this.credentialStore = options.credentialStore ?? new InMemoryCredentialStore();
    this.transport = options.transport ?? defaultTransport;
  }
  // MARK: - Authentication Status
  /// Check if a valid credential exists (or one that can still be refreshed).
  async hasValidCredential() {
    const credential = await this.credentialStore.load(OPENAI_CODEX_PROVIDER);
    if (!credential) return false;
    return !credentialIsExpired(credential) || credential.refreshToken.length > 0;
  }
  /// Check if the user is currently signed in (has any credential).
  async isSignedIn() {
    return this.credentialStore.hasCredential(OPENAI_CODEX_PROVIDER);
  }
  /// Get user info from the stored credential.
  async userEmail() {
    return (await this.credentialStore.load(OPENAI_CODEX_PROVIDER))?.email ?? null;
  }
  // MARK: - Authentication
  /**
   * Build the PKCE authorization URL. The host opens this in a browser,
   * captures the `code` query parameter from the
   * `axonproviderkit://oauth-callback` redirect, and calls `exchangeCode`.
   */
  async buildAuthorizationUrl() {
    const codeVerifier = generateCodeVerifier();
    const challenge = await computeCodeChallenge(codeVerifier);
    const state = generateUUID();
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: OPENAI_CODEX_CALLBACK_URL,
      response_type: "code",
      scope: "openid profile email offline_access",
      code_challenge: challenge,
      code_challenge_method: "S256",
      state
    });
    return {
      url: `${AUTHORIZATION_ENDPOINT}?${params.toString()}`,
      codeVerifier,
      state
    };
  }
  /**
   * Extract the authorization code from a redirect URL, or null.
   * Convenience for hosts handling the `axonproviderkit://oauth-callback` redirect.
   */
  static authorizationCodeFromCallback(callbackUrl) {
    try {
      const url = new URL(callbackUrl);
      return url.searchParams.get("code");
    } catch {
      return null;
    }
  }
  /**
   * Exchange an authorization code for tokens and store the credential.
   */
  async exchangeCode(options) {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code: options.code,
      redirect_uri: OPENAI_CODEX_CALLBACK_URL,
      code_verifier: options.codeVerifier
    });
    const response = await this.transport({
      url: TOKEN_ENDPOINT,
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString()
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw OAuthError.authenticationFailed(
        `Token exchange failed (${response.status}): ${responseBody || "Unknown error"}`
      );
    }
    const credential = parseTokenResponse(responseBody);
    await this.credentialStore.save(credential);
    return credential;
  }
  /// Sign out and remove stored credentials.
  async signOut() {
    await this.credentialStore.delete(OPENAI_CODEX_PROVIDER);
  }
  // MARK: - Token Management
  /// Get a valid access token, refreshing if necessary.
  async getAccessToken() {
    return (await this.getCredential()).accessToken;
  }
  /// Get the stored credential, refreshing if needed.
  async getCredential() {
    let credential = await this.credentialStore.load(OPENAI_CODEX_PROVIDER);
    if (!credential) {
      throw OAuthError.noCredential();
    }
    if (credentialNeedsRefresh(credential)) {
      credential = await this.refreshCredential(credential);
      await this.credentialStore.save(credential);
    }
    return credential;
  }
  /// Refresh an expired credential.
  async refreshCredential(credential) {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      refresh_token: credential.refreshToken
    });
    const response = await this.transport({
      url: TOKEN_ENDPOINT,
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString()
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw OAuthError.refreshFailed(
        `Token refresh failed (${response.status}): ${responseBody || "Unknown error"}`
      );
    }
    const refreshed = parseTokenResponse(responseBody);
    return {
      provider: refreshed.provider,
      accessToken: refreshed.accessToken,
      refreshToken: refreshed.refreshToken.length > 0 ? refreshed.refreshToken : credential.refreshToken,
      expiresAt: refreshed.expiresAt,
      email: refreshed.email ?? credential.email,
      accountId: refreshed.accountId ?? credential.accountId
    };
  }
};
function parseTokenResponse(body) {
  let response;
  try {
    response = JSON.parse(body);
  } catch {
    throw OAuthError.invalidResponse("Token response is not valid JSON");
  }
  if (typeof response.access_token !== "string") {
    throw OAuthError.invalidResponse("Token response missing access_token");
  }
  let email = typeof response.email === "string" ? response.email : void 0;
  if (email === void 0 && typeof response.id_token === "string") {
    email = parseEmailFromIdToken(response.id_token) ?? void 0;
  }
  return credentialFromTokenResponse({
    provider: OPENAI_CODEX_PROVIDER,
    accessToken: response.access_token,
    refreshToken: typeof response.refresh_token === "string" ? response.refresh_token : "",
    expiresIn: typeof response.expires_in === "number" ? response.expires_in : 3600,
    email
  });
}
function parseEmailFromIdToken(idToken) {
  const parts = idToken.split(".");
  if (parts.length < 2) return null;
  let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  while (payload.length % 4 !== 0) {
    payload += "=";
  }
  try {
    const decoded = JSON.parse(atob(payload));
    if (typeof decoded === "object" && decoded !== null && "email" in decoded && typeof decoded.email === "string") {
      return decoded.email;
    }
    return null;
  } catch {
    return null;
  }
}

// src/client/oauth/oauth-manager.ts
var OAuthProvider = {
  /// OpenAI Codex (ChatGPT Plus/Pro subscriptions).
  openaiCodex: OPENAI_CODEX_PROVIDER
};
var ALL_OAUTH_PROVIDERS = [OAuthProvider.openaiCodex];
function oauthProviderDisplayName(provider) {
  switch (provider) {
    case OAuthProvider.openaiCodex:
      return "ChatGPT (Plus/Pro)";
  }
}
function oauthProviderDescription(provider) {
  switch (provider) {
    case OAuthProvider.openaiCodex:
      return "Use your ChatGPT Plus or Pro subscription for AI access";
  }
}
var OAuthManager = class {
  /// OpenAI Codex authenticator.
  openAICodex;
  constructor(options = {}) {
    this.openAICodex = new OpenAICodexOAuth(options);
  }
  // MARK: - Authentication Status
  /// Get authentication status for a provider.
  async authenticationStatus(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex: {
        const signedIn = await this.openAICodex.isSignedIn();
        if (!signedIn) return "notAuthenticated";
        const credential = await this.credentialFor(provider);
        if (credential && credentialNeedsRefresh(credential)) {
          return "needsRefresh";
        }
        return "authenticated";
      }
    }
  }
  async credentialFor(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex:
        return this.openAICodex.isSignedIn().then(
          async (signedIn) => signedIn ? this.openAICodex.getCredential().catch(() => null) : null
        );
    }
  }
  /// Check if user is authenticated with a provider (even if refresh is needed).
  async isAuthenticated(provider) {
    const status = await this.authenticationStatus(provider);
    return status === "authenticated" || status === "needsRefresh";
  }
  /// Get user's email for a provider if available.
  async userEmail(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex:
        return this.openAICodex.userEmail();
    }
  }
  // MARK: - Sign out
  /// Sign out from an OAuth provider.
  async signOut(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex:
        await this.openAICodex.signOut();
    }
  }
  /// Sign out from all OAuth providers.
  async signOutAll() {
    for (const provider of ALL_OAUTH_PROVIDERS) {
      await this.signOut(provider);
    }
  }
  // MARK: - Token Access
  /// Get a valid access token for a provider (auto-refreshes if needed).
  async getAccessToken(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex:
        return this.openAICodex.getAccessToken();
    }
  }
  /// Get the full credential for a provider (auto-refreshes if needed).
  async getCredential(provider) {
    switch (provider) {
      case OAuthProvider.openaiCodex:
        return this.openAICodex.getCredential();
    }
  }
  // MARK: - Provider Discovery
  /// Get all providers the user is authenticated with.
  async authenticatedProviders() {
    const providers = [];
    for (const provider of ALL_OAUTH_PROVIDERS) {
      if (await this.isAuthenticated(provider)) {
        providers.push(provider);
      }
    }
    return providers;
  }
};
async function sendWithOAuth(client, manager, options) {
  const token = await manager.getAccessToken(OAuthProvider.openaiCodex);
  return client.send({
    messages: options.messages,
    provider: AIProvider.openai,
    model: options.model ?? "gpt-4o",
    apiKey: token,
    system: options.system,
    maxTokens: options.maxTokens,
    settings: options.settings,
    signal: options.signal
  });
}
async function* streamWithOAuth(client, manager, options) {
  const token = await manager.getAccessToken(OAuthProvider.openaiCodex);
  yield* client.stream({
    messages: options.messages,
    provider: AIProvider.openai,
    model: options.model ?? "gpt-4o",
    apiKey: token,
    system: options.system,
    maxTokens: options.maxTokens,
    settings: options.settings,
    signal: options.signal
  });
}

// src/client/services/anthropic-content-replay.ts
var REPLAY_BLOCK_TYPES = ["text", "thinking", "redacted_thinking", "tool_use"];
function asObjectArray(value) {
  if (!Array.isArray(value)) return void 0;
  return value.every(isJSONObject) ? value : void 0;
}
function sortedKeysJSON(value) {
  return JSON.stringify(value, (_key, nested) => {
    if (!isJSONObject(nested)) return nested;
    const sorted = {};
    for (const key of Object.keys(nested).sort()) sorted[key] = nested[key];
    return sorted;
  });
}
function jsonEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((element, index) => jsonEqual(element, b[index]));
  }
  if (isJSONObject(a) && isJSONObject(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && jsonEqual(a[key], b[key]));
  }
  return false;
}
function parseJSON(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return void 0;
  }
}
function replayMetadata(blocks) {
  if (blocks.length === 0) return void 0;
  return { anthropicContentJSON: sortedKeysJSON(blocks) };
}
var AnthropicContentReplayAccumulator = class {
  blocks = /* @__PURE__ */ new Map();
  arguments = /* @__PURE__ */ new Map();
  consume(event) {
    const json = parseJSON(event);
    if (!isJSONObject(json)) return;
    const index = json["index"];
    if (typeof index !== "number" || !Number.isInteger(index)) return;
    switch (json["type"]) {
      case "content_block_start": {
        const block = json["content_block"];
        if (isJSONObject(block)) {
          this.blocks.set(index, block);
        } else {
          this.blocks.delete(index);
        }
        break;
      }
      case "content_block_delta": {
        const block = this.blocks.get(index);
        const delta = json["delta"];
        if (!block || !isJSONObject(delta)) return;
        const append = (key) => {
          const existing = typeof block[key] === "string" ? block[key] : "";
          const addition = typeof delta[key] === "string" ? delta[key] : "";
          block[key] = existing + addition;
        };
        switch (delta["type"]) {
          case "text_delta":
            append("text");
            break;
          case "thinking_delta":
            append("thinking");
            break;
          case "signature_delta":
            append("signature");
            break;
          case "input_json_delta":
            this.arguments.set(
              index,
              (this.arguments.get(index) ?? "") + (typeof delta["partial_json"] === "string" ? delta["partial_json"] : "")
            );
            break;
        }
        break;
      }
      case "content_block_stop": {
        const raw = this.arguments.get(index);
        if (raw === void 0) break;
        const input = parseJSON(raw);
        const block = this.blocks.get(index);
        if (block && (isJSONObject(input) || Array.isArray(input))) {
          block["input"] = input;
        }
        break;
      }
    }
  }
  get metadata() {
    const ordered = [...this.blocks.keys()].sort((a, b) => a - b).map((key) => this.blocks.get(key));
    return replayMetadata(ordered);
  }
};
var AnthropicContentReplay = {
  metadata(blocks) {
    return replayMetadata(blocks);
  },
  validatedBlocks(message) {
    if (message.role !== "assistant") return null;
    const raw = message.providerMetadata?.anthropicContentJSON;
    if (raw === void 0) return null;
    const blocks = asObjectArray(parseJSON(raw));
    if (!blocks || blocks.length === 0) return null;
    if (!blocks.every(
      (block) => REPLAY_BLOCK_TYPES.includes(typeof block["type"] === "string" ? block["type"] : "")
    )) {
      return null;
    }
    const text = blocks.filter((block) => block["type"] === "text").flatMap((block) => typeof block["text"] === "string" ? [block["text"]] : []).join("");
    if (text !== message.content) return null;
    const toolBlocks = blocks.filter((block) => block["type"] === "tool_use");
    const calls = message.toolCalls ?? [];
    if (toolBlocks.length !== calls.length) return null;
    for (let index = 0; index < toolBlocks.length; index++) {
      const block = toolBlocks[index];
      const call = calls[index];
      if (block["id"] !== call.id || block["name"] !== call.name) return null;
      const input = parseJSON(call.arguments);
      const original = block["input"];
      if (!isJSONObject(input) || !isJSONObject(original) || !jsonEqual(input, original)) {
        return null;
      }
    }
    return blocks;
  },
  Accumulator: AnthropicContentReplayAccumulator
};

// src/client/utilities/openai-content-formatter.ts
var OpenAIContentFormatter = {
  /**
   * Content for a message: a plain string when there are no attachments,
   * otherwise an array of content parts.
   */
  content(message) {
    const attachments = message.attachments ?? [];
    const trimmedText = message.content.trim();
    if (attachments.length === 0) {
      return trimmedText;
    }
    const parts = [];
    if (trimmedText.length > 0) {
      parts.push({ type: "text", text: trimmedText });
    }
    for (const attachment of attachments) {
      const mimeType = attachment.mimeType ?? defaultMimeType(attachment.type);
      switch (attachment.type) {
        case "image":
          if (attachment.base64) {
            parts.push({
              type: "image_url",
              image_url: {
                url: `data:${mimeType};base64,${attachment.base64}`,
                detail: "high"
              }
            });
          } else if (attachment.url) {
            parts.push({
              type: "image_url",
              image_url: { url: attachment.url, detail: "high" }
            });
          }
          break;
        case "document":
          if (attachment.base64) {
            parts.push({
              type: "file",
              file: {
                filename: attachment.name ?? defaultFileName(mimeType),
                file_data: `data:${mimeType};base64,${attachment.base64}`
              }
            });
          }
          break;
        case "audio":
          if (attachment.base64) {
            const format = mimeType.split("/").pop() ?? "wav";
            parts.push({
              type: "input_audio",
              input_audio: { data: attachment.base64, format }
            });
          }
          break;
        case "video":
          continue;
      }
    }
    return parts.length === 0 ? trimmedText : parts;
  },
  hasSerializableContent(content) {
    if (typeof content === "string") {
      return content.trim().length > 0;
    }
    if (Array.isArray(content)) {
      return content.length > 0;
    }
    return true;
  }
};
function defaultMimeType(type) {
  switch (type) {
    case "image":
      return "image/jpeg";
    case "document":
      return "application/pdf";
    case "audio":
      return "audio/wav";
    case "video":
      return "video/mp4";
  }
}
function defaultFileName(mimeType) {
  switch (mimeType) {
    case "application/pdf":
      return "attachment.pdf";
    case "text/plain":
      return "attachment.txt";
    case "text/markdown":
      return "attachment.md";
    case "text/csv":
      return "attachment.csv";
    case "application/json":
      return "attachment.json";
    default:
      return "attachment";
  }
}

// src/client/services/message-builders.ts
var GEMINI_THOUGHT_SIGNATURE_FALLBACK = "skip_thought_signature_validator";
function anthropicContentBlocks(message) {
  const blocks = [];
  const trimmedText = message.content.trim();
  if (trimmedText.length > 0) {
    blocks.push({ type: "text", text: trimmedText });
  }
  for (const attachment of message.attachments ?? []) {
    const mimeType = attachment.mimeType ?? "image/jpeg";
    switch (attachment.type) {
      case "image":
        if (attachment.base64) {
          blocks.push({
            type: "image",
            source: { type: "base64", media_type: mimeType, data: attachment.base64 }
          });
        } else if (attachment.url) {
          blocks.push({
            type: "image",
            source: { type: "url", url: attachment.url }
          });
        }
        break;
      case "document":
        if (attachment.base64) {
          blocks.push({
            type: "document",
            source: { type: "base64", media_type: mimeType, data: attachment.base64 }
          });
        }
        break;
      case "audio":
      case "video":
        continue;
    }
  }
  return blocks;
}
function buildAnthropicMessages(messages) {
  const apiMessages = [];
  for (const msg of messages) {
    if (msg.role === "system") continue;
    if (msg.role === "tool" && msg.toolCallId) {
      apiMessages.push({
        role: "user",
        content: [{ type: "tool_result", tool_use_id: msg.toolCallId, content: msg.content }]
      });
      continue;
    }
    const role = msg.role === "assistant" ? "assistant" : "user";
    const replay = AnthropicContentReplay.validatedBlocks(msg);
    if (replay) {
      apiMessages.push({ role, content: replay });
      continue;
    }
    if (msg.role === "assistant" && msg.toolCalls && msg.toolCalls.length > 0) {
      const contentBlocks2 = anthropicContentBlocks(msg);
      for (const toolCall of msg.toolCalls) {
        const toolUseBlock = {
          type: "tool_use",
          id: toolCall.id,
          name: toolCall.name
        };
        try {
          toolUseBlock["input"] = JSON.parse(toolCall.arguments);
        } catch {
          toolUseBlock["input"] = {};
        }
        contentBlocks2.push(toolUseBlock);
      }
      apiMessages.push({ role, content: contentBlocks2 });
      continue;
    }
    const contentBlocks = anthropicContentBlocks(msg);
    if (contentBlocks.length === 0) continue;
    apiMessages.push({ role, content: contentBlocks });
  }
  return apiMessages;
}
function buildOpenAICompatibleMessages(messages, system) {
  const apiMessages = [];
  if (system && system.length > 0) {
    apiMessages.push({ role: "system", content: system });
  }
  for (const msg of messages) {
    if (msg.role === "system") continue;
    if (msg.role === "tool" && msg.toolCallId) {
      apiMessages.push({
        role: "tool",
        tool_call_id: msg.toolCallId,
        content: msg.content
      });
      continue;
    }
    if (msg.role === "assistant" && msg.toolCalls && msg.toolCalls.length > 0) {
      const assistantMessage = { role: "assistant" };
      const trimmedContent = msg.content.trim();
      if (trimmedContent.length > 0) {
        assistantMessage["content"] = trimmedContent;
      }
      assistantMessage["tool_calls"] = msg.toolCalls.map((toolCall) => ({
        id: toolCall.id,
        type: "function",
        function: {
          name: toolCall.name,
          arguments: toolCall.arguments
        }
      }));
      apiMessages.push(assistantMessage);
      continue;
    }
    const content = OpenAIContentFormatter.content(msg);
    if (!OpenAIContentFormatter.hasSerializableContent(content)) {
      continue;
    }
    apiMessages.push({ role: msg.role, content });
  }
  return apiMessages;
}
function geminiParts(message, includeEmptyFallback = true) {
  const parts = [];
  const trimmedText = message.content.trim();
  if (trimmedText.length > 0) {
    parts.push({ text: trimmedText });
  }
  for (const attachment of message.attachments ?? []) {
    const mimeType = attachment.mimeType ?? "image/jpeg";
    if (attachment.base64) {
      parts.push({
        inline_data: { mime_type: mimeType, data: attachment.base64 }
      });
    } else if (attachment.url) {
      parts.push({
        file_data: { file_uri: attachment.url, mime_type: mimeType }
      });
    }
  }
  if (parts.length === 0 && includeEmptyFallback) {
    parts.push({ text: "" });
  }
  return parts;
}
function geminiFunctionCallPart(toolCall, model) {
  let args = {};
  try {
    const parsed = JSON.parse(toolCall.arguments);
    if (isJSONObject(parsed)) {
      args = parsed;
    }
  } catch {
  }
  const part = {
    functionCall: {
      name: toolCall.name,
      args
    }
  };
  if (toolCall.thoughtSignature && toolCall.thoughtSignature.trim().length > 0) {
    part["thoughtSignature"] = toolCall.thoughtSignature;
  } else if (shouldUseGeminiThoughtSignatureFallback(model)) {
    part["thoughtSignature"] = GEMINI_THOUGHT_SIGNATURE_FALLBACK;
  }
  return part;
}
function shouldUseGeminiThoughtSignatureFallback(model) {
  if (!model) return false;
  return model.toLowerCase().includes("gemini-3");
}
function geminiFunctionResponsePart(message) {
  if (message.role !== "tool" || !message.toolName) {
    return null;
  }
  return {
    functionResponse: {
      name: message.toolName,
      response: geminiResponseObject(message.content)
    }
  };
}
function geminiResponseObject(content) {
  try {
    return JSON.parse(content);
  } catch {
    return { result: content };
  }
}
function buildGeminiContents(messages, model) {
  const contents = [];
  let index = 0;
  while (index < messages.length) {
    const message = messages[index];
    if (message.role === "system") {
      index += 1;
      continue;
    }
    if (message.role === "tool") {
      const parts = [];
      while (index < messages.length) {
        const toolMessage = messages[index];
        if (toolMessage.role !== "tool") break;
        const part = geminiFunctionResponsePart(toolMessage);
        if (part) {
          parts.push(part);
        }
        index += 1;
      }
      if (parts.length > 0) {
        contents.push({ role: "user", parts });
      }
      continue;
    }
    const role = message.role === "assistant" ? "model" : "user";
    if (message.role === "assistant" && message.toolCalls && message.toolCalls.length > 0) {
      const parts = geminiParts(message, false);
      for (const toolCall of message.toolCalls) {
        parts.push(geminiFunctionCallPart(toolCall, model));
      }
      if (parts.length > 0) {
        contents.push({ role, parts });
      }
      index += 1;
      continue;
    }
    contents.push({ role, parts: geminiParts(message) });
    index += 1;
  }
  return contents;
}

// src/client/services/provider-prompt-caching.ts
var breakpoint = () => ({ type: "ephemeral" });
var number = (value) => typeof value === "number" ? value : void 0;
var ProviderPromptCaching = {
  anthropicSystem(system) {
    return [{ type: "text", text: system, cache_control: breakpoint() }];
  },
  anthropicTools(tools) {
    return tools.map((tool, index) => index === tools.length - 1 ? { ...tool, cache_control: breakpoint() } : tool);
  },
  anthropicMessages(messages) {
    const last = messages.at(-1);
    if (!last) return messages;
    const content = last["content"];
    const blocks = typeof content === "string" ? content.length ? [{ type: "text", text: content }] : [] : Array.isArray(content) && content.every(isJSONObject) ? content : [];
    let index = blocks.length - 1;
    while (index >= 0) {
      const block = blocks[index];
      if (block["type"] !== "thinking" && block["type"] !== "redacted_thinking" && (block["type"] !== "text" || typeof block["text"] === "string" && block["text"].length > 0)) break;
      index -= 1;
    }
    if (index < 0) return messages;
    return [...messages.slice(0, -1), { ...last, content: blocks.map((block, i) => i === index ? { ...block, cache_control: breakpoint() } : block) }];
  },
  openAIUsage(usage) {
    const input = number(usage["input_tokens"]) ?? number(usage["prompt_tokens"]) ?? 0;
    const output = number(usage["output_tokens"]) ?? number(usage["completion_tokens"]) ?? 0;
    const details = usage["input_tokens_details"] ?? usage["prompt_tokens_details"];
    const cached = (isJSONObject(details) ? number(details["cached_tokens"]) : void 0) ?? number(usage["prompt_cache_hit_tokens"]);
    return tokenUsage(input, output, number(usage["total_tokens"]), cached);
  },
  anthropicUsage(start, delta) {
    if (!start && !delta) return void 0;
    const field = (key) => number(delta?.[key]) ?? number(start?.[key]);
    const read = field("cache_read_input_tokens");
    const write = field("cache_creation_input_tokens");
    return tokenUsage(
      (field("input_tokens") ?? 0) + (read ?? 0) + (write ?? 0),
      field("output_tokens") ?? 0,
      void 0,
      read,
      write
    );
  }
};

// src/client/extensions/openai/openai-responses.ts
function combinedText(response) {
  if (response.outputText !== void 0 && response.outputText.length > 0) {
    return response.outputText;
  }
  let joined = "";
  for (const output of response.output ?? []) {
    for (const content of output.content ?? []) {
      if (content.text !== void 0) joined += content.text;
    }
  }
  return joined.length > 0 ? joined : void 0;
}
function asRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
}
function asString(value) {
  return typeof value === "string" ? value : void 0;
}
function asNumber(value) {
  return typeof value === "number" ? value : void 0;
}
function asBoolean(value) {
  return typeof value === "boolean" ? value : void 0;
}
function asArray(value) {
  return Array.isArray(value) ? value : void 0;
}
function asStringArray(value) {
  if (!Array.isArray(value)) return void 0;
  const out = [];
  for (const entry of value) if (typeof entry === "string") out.push(entry);
  return out;
}
function serializeResponsesInput(input) {
  if (typeof input === "string") return input;
  return input.map(serializeResponsesInputItem);
}
function serializeResponsesInputItem(item) {
  const body = {};
  if (item.role !== void 0) body["role"] = item.role;
  if (item.content !== void 0) body["content"] = item.content.map(serializeResponsesContent);
  if (item.type !== void 0) body["type"] = item.type;
  if (item.callId !== void 0) body["call_id"] = item.callId;
  if (item.output !== void 0) body["output"] = serializeResponsesOutputPayload(item.output);
  if (item.acknowledgedSafetyChecks !== void 0) {
    body["acknowledged_safety_checks"] = item.acknowledgedSafetyChecks.map(serializeSafetyCheck);
  }
  if (item.currentUrl !== void 0) body["current_url"] = item.currentUrl;
  return body;
}
function serializeResponsesContent(content) {
  const body = { type: content.type };
  if (content.text !== void 0) body["text"] = content.text;
  if (content.imageUrl !== void 0) body["image_url"] = content.imageUrl;
  return body;
}
function serializeResponsesOutputPayload(payload) {
  const body = { type: payload.type };
  if (payload.imageUrl !== void 0) body["image_url"] = payload.imageUrl;
  return body;
}
function serializeSafetyCheck(check) {
  const body = {};
  if (check.id !== void 0) body["id"] = check.id;
  if (check.code !== void 0) body["code"] = check.code;
  if (check.message !== void 0) body["message"] = check.message;
  return body;
}
function serializeResponsesReasoning(reasoning) {
  const body = {};
  if (reasoning.summary !== void 0) body["summary"] = reasoning.summary;
  return body;
}
function serializeApproximateLocation(location) {
  const body = {};
  if (location.country !== void 0) body["country"] = location.country;
  if (location.city !== void 0) body["city"] = location.city;
  if (location.region !== void 0) body["region"] = location.region;
  if (location.timezone !== void 0) body["timezone"] = location.timezone;
  return body;
}
function serializeUserLocation(location) {
  const body = { type: location.type };
  if (location.approximate !== void 0) {
    body["approximate"] = serializeApproximateLocation(location.approximate);
  }
  return body;
}
function serializeWebSearchFilters(filters) {
  const body = {};
  if (filters.domains !== void 0) body["domains"] = [...filters.domains];
  return body;
}
function serializeResponsesTool(tool) {
  const body = { type: tool.type };
  if (tool.displayWidth !== void 0) body["display_width"] = tool.displayWidth;
  if (tool.displayHeight !== void 0) body["display_height"] = tool.displayHeight;
  if (tool.environment !== void 0) body["environment"] = tool.environment;
  if (tool.vectorStoreIds !== void 0) body["vector_store_ids"] = [...tool.vectorStoreIds];
  if (tool.container !== void 0) body["container"] = { type: tool.container.type };
  if (tool.serverLabel !== void 0) body["server_label"] = tool.serverLabel;
  if (tool.serverUrl !== void 0) body["server_url"] = tool.serverUrl;
  if (tool.requireApproval !== void 0) body["require_approval"] = tool.requireApproval;
  if (tool.externalWebAccess !== void 0) body["external_web_access"] = tool.externalWebAccess;
  if (tool.userLocation !== void 0) body["user_location"] = serializeUserLocation(tool.userLocation);
  if (tool.filters !== void 0) body["filters"] = serializeWebSearchFilters(tool.filters);
  if (tool.connectorId !== void 0) body["connector_id"] = tool.connectorId;
  if (tool.allowedTools !== void 0) body["allowed_tools"] = [...tool.allowedTools];
  if (tool.maxNumResults !== void 0) body["max_num_results"] = tool.maxNumResults;
  if (tool.searchContextSize !== void 0) body["search_context_size"] = tool.searchContextSize;
  if (tool.sandbox !== void 0) {
    body["sandbox"] = { type: tool.sandbox.type, ...tool.sandbox.networkAccess === void 0 ? {} : { network_access: tool.sandbox.networkAccess } };
  }
  if (tool.timeout !== void 0) body["timeout"] = tool.timeout;
  if (tool.skillIds !== void 0) body["skill_ids"] = [...tool.skillIds];
  return body;
}
function serializeResponsesRequest(request) {
  const body = {
    model: request.model,
    input: serializeResponsesInput(request.input)
  };
  if (request.tools !== void 0) body["tools"] = request.tools.map(serializeResponsesTool);
  if (request.background !== void 0) body["background"] = request.background;
  if (request.maxToolCalls !== void 0) body["max_tool_calls"] = request.maxToolCalls;
  if (request.reasoning !== void 0) body["reasoning"] = serializeResponsesReasoning(request.reasoning);
  if (request.truncation !== void 0) body["truncation"] = request.truncation;
  if (request.previousResponseId !== void 0) body["previous_response_id"] = request.previousResponseId;
  if (request.store !== void 0) body["store"] = request.store;
  if (request.instructions !== void 0) body["instructions"] = request.instructions;
  if (request.include !== void 0) body["include"] = [...request.include];
  if (request.parallelToolCalls !== void 0) body["parallel_tool_calls"] = request.parallelToolCalls;
  if (request.metadata !== void 0) body["metadata"] = { ...request.metadata };
  return body;
}
function decodeResponsesCitation(record) {
  return {
    url: asString(record["url"]),
    title: asString(record["title"]),
    startIndex: asNumber(record["start_index"]),
    endIndex: asNumber(record["end_index"])
  };
}
function decodeResponsesAnnotation(record) {
  const citation = asRecord(record["url_citation"]);
  return {
    type: asString(record["type"]),
    urlCitation: citation ? decodeResponsesCitation(citation) : void 0
  };
}
function decodeResponsesOutputContent(record) {
  const annotations = asArray(record["annotations"]);
  return {
    type: asString(record["type"]),
    text: asString(record["text"]),
    annotations: annotations ? annotations.flatMap((entry) => {
      const rec = asRecord(entry);
      return rec ? [decodeResponsesAnnotation(rec)] : [];
    }) : void 0
  };
}
function decodeComputerAction(record) {
  return {
    type: asString(record["type"]) ?? "",
    x: asNumber(record["x"]),
    y: asNumber(record["y"]),
    button: asString(record["button"]),
    scrollX: asNumber(record["scroll_x"]),
    scrollY: asNumber(record["scroll_y"]),
    text: asString(record["text"]),
    keys: asStringArray(record["keys"])
  };
}
function decodeResponsesAction(record) {
  return {
    type: asString(record["type"]),
    query: asString(record["query"]),
    url: asString(record["url"]),
    id: asString(record["id"])
  };
}
function decodeSafetyCheck(record) {
  return {
    id: asString(record["id"]),
    code: asString(record["code"]),
    message: asString(record["message"])
  };
}
function decodeResponsesOutput(record) {
  const content = asArray(record["content"]);
  const pendingSafetyChecks = asArray(record["pending_safety_checks"]);
  const action = asRecord(record["action"]);
  const rawAction = asRecord(record["raw_action"]);
  const result = asRecord(record["result"]);
  return {
    id: asString(record["id"]),
    type: asString(record["type"]),
    status: asString(record["status"]),
    content: content ? content.flatMap((entry) => {
      const rec = asRecord(entry);
      return rec ? [decodeResponsesOutputContent(rec)] : [];
    }) : void 0,
    callId: asString(record["call_id"]),
    action: action ? decodeComputerAction(action) : void 0,
    pendingSafetyChecks: pendingSafetyChecks ? pendingSafetyChecks.flatMap((entry) => {
      const rec = asRecord(entry);
      return rec ? [decodeSafetyCheck(rec)] : [];
    }) : void 0,
    rawAction: rawAction ? decodeResponsesAction(rawAction) : void 0,
    name: asString(record["name"]),
    arguments: asString(record["arguments"]),
    result: result ? decodeResponsesToolResult(result) : void 0,
    approvalRequestId: asString(record["approval_request_id"])
  };
}
function decodeResponsesResponse(record) {
  const output = asArray(record["output"]);
  const error = asRecord(record["error"]);
  return {
    id: asString(record["id"]),
    status: asString(record["status"]),
    output: output ? output.flatMap((entry) => {
      const rec = asRecord(entry);
      return rec ? [decodeResponsesOutput(rec)] : [];
    }) : void 0,
    outputText: asString(record["output_text"]),
    error: error ? decodeResponsesFailure(error) : void 0,
    background: asBoolean(record["background"])
  };
}
function decodeResponsesToolResult(record) {
  const files = asArray(record["files"]);
  return {
    type: asString(record["type"]),
    output: asString(record["output"]),
    exitCode: asNumber(record["exit_code"]),
    files: files?.flatMap((entry) => {
      const file = asRecord(entry);
      return file ? [{ id: asString(file["id"]), filename: asString(file["filename"]), mimeType: asString(file["mime_type"]) }] : [];
    })
  };
}
function decodeResponsesFailure(record) {
  return { code: asString(record["code"]), message: asString(record["message"]), param: asString(record["param"]) };
}

// src/client/services/tool-formatters.ts
function formatProperties(properties) {
  const result = {};
  for (const [name, prop] of Object.entries(properties)) {
    const propDict = {
      type: prop.type,
      description: prop.description
    };
    if (prop.enumValues) {
      propDict["enum"] = [...prop.enumValues];
    }
    if (prop.items) {
      propDict["items"] = { type: prop.items.type };
    }
    result[name] = propDict;
  }
  return result;
}
function formatPropertiesForGemini(properties) {
  const result = {};
  for (const [name, prop] of Object.entries(properties)) {
    const propDict = {
      type: geminiType(prop.type),
      description: prop.description
    };
    if (prop.enumValues) {
      propDict["enum"] = [...prop.enumValues];
    }
    if (prop.items) {
      propDict["items"] = { type: geminiType(prop.items.type) };
    }
    result[name] = propDict;
  }
  return result;
}
function geminiType(type) {
  return type.toUpperCase();
}
var ToolFormatters = {
  /**
   * JSON Schema object for tool parameters, matching the Swift
   * `JSONEncoder().encode(tool.parameters)` shape (`{type, properties, required}`).
   */
  toolParametersSchema(parameters) {
    return {
      type: parameters.type,
      properties: formatProperties(parameters.properties),
      required: [...parameters.required]
    };
  },
  // MARK: - Anthropic
  /**
   * Convert tools to Anthropic's format
   * (`{name, description, input_schema: {type, properties, required}}`).
   */
  anthropicTools(tools) {
    return tools.map((tool) => ({
      name: tool.name,
      description: tool.description,
      input_schema: {
        type: "object",
        properties: formatProperties(tool.parameters.properties),
        required: [...tool.parameters.required]
      }
    }));
  },
  /// Convert tool choice to Anthropic format. `none` returns null (don't send tools at all).
  anthropicToolChoice(choice) {
    if (!choice) return null;
    switch (choice.type) {
      case "auto":
        return { type: "auto" };
      case "required":
        return { type: "any" };
      case "none":
        return null;
      case "specific":
        return { type: "tool", name: choice.toolName };
    }
  },
  // MARK: - OpenAI
  /**
   * Convert tools to OpenAI's format
   * (`{type: "function", function: {name, description, parameters}}`).
   */
  openAITools(tools) {
    return tools.map((tool) => ({
      type: "function",
      function: {
        name: tool.name,
        description: tool.description,
        parameters: {
          type: "object",
          properties: formatProperties(tool.parameters.properties),
          required: [...tool.parameters.required]
        }
      }
    }));
  },
  /// Convert tool choice to OpenAI format.
  openAIToolChoice(choice) {
    if (!choice) return null;
    switch (choice.type) {
      case "auto":
        return "auto";
      case "required":
        return "required";
      case "none":
        return "none";
      case "specific":
        return { type: "function", function: { name: choice.toolName } };
    }
  },
  // MARK: - Gemini
  /**
   * Convert tools to Gemini's format
   * (`[{function_declarations: [{name, description, parameters}]}]`, with
   * uppercased JSON-schema types).
   */
  geminiTools(tools) {
    const functionDeclarations = tools.map((tool) => {
      const decl = {
        name: tool.name,
        description: tool.description
      };
      if (Object.keys(tool.parameters.properties).length > 0) {
        decl["parameters"] = {
          type: "object",
          properties: formatPropertiesForGemini(tool.parameters.properties),
          required: [...tool.parameters.required]
        };
      }
      return decl;
    });
    return [{ function_declarations: functionDeclarations }];
  },
  /// Convert tool choice to Gemini's toolConfig format.
  geminiToolConfig(choice) {
    if (!choice) return null;
    switch (choice.type) {
      case "auto":
        return { function_calling_config: { mode: "AUTO" } };
      case "required":
        return { function_calling_config: { mode: "ANY" } };
      case "none":
        return { function_calling_config: { mode: "NONE" } };
      case "specific":
        return {
          function_calling_config: { mode: "ANY", allowed_function_names: [choice.toolName] }
        };
    }
  },
  // MARK: - Grok (xAI) — OpenAI-compatible
  grokTools(tools) {
    return this.openAITools(tools);
  },
  grokToolChoice(choice) {
    return this.openAIToolChoice(choice);
  },
  // MARK: - Mistral — OpenAI-compatible
  mistralTools(tools) {
    return this.openAITools(tools);
  },
  mistralToolChoice(choice) {
    return this.openAIToolChoice(choice);
  },
  // MARK: - Tool Result Formatters
  /**
   * Format tool results for the Anthropic API: a single message with role
   * "user" containing tool_result blocks.
   */
  anthropicToolResults(results) {
    const content = results.map((result) => {
      const block = {
        type: "tool_result",
        tool_use_id: result.toolCallId,
        content: result.result
      };
      if (result.isError) {
        block["is_error"] = true;
      }
      return block;
    });
    return [{ role: "user", content }];
  },
  /// Format tool results for the OpenAI API: an array of messages with role "tool".
  openAIToolResults(results) {
    return results.map((result) => ({
      role: "tool",
      tool_call_id: result.toolCallId,
      content: result.result
    }));
  },
  /**
   * Format tool results for the Gemini API: a user content entry with
   * functionResponse parts. Results that parse as JSON are passed through;
   * plain strings are wrapped in `{result}`.
   */
  geminiToolResults(results) {
    const parts = results.map((result) => {
      let response;
      try {
        response = JSON.parse(result.result);
      } catch {
        response = { result: result.result };
      }
      return {
        functionResponse: {
          name: result.toolName,
          response
        }
      };
    });
    return [{ role: "user", parts }];
  }
};

// src/client/services/openai-chat-responses.ts
function asObjectArray2(value) {
  if (!Array.isArray(value)) return void 0;
  return value.every(isJSONObject) ? value : void 0;
}
function asInt(value) {
  return typeof value === "number" && Number.isInteger(value) ? value : void 0;
}
function toolCallsEqual(a, b) {
  return a.length === b.length && a.every(
    (call, index) => call.id === b[index].id && call.name === b[index].name && call.arguments === b[index].arguments && call.thoughtSignature === b[index].thoughtSignature
  );
}
var REPLAY_ITEM_TYPES = ["message", "reasoning", "function_call"];
var MUSE_AUDIO_MIME_TYPES = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav"];
var OpenAIChatResponses = {
  body(options) {
    const { messages, model, maxTokens } = options;
    const parameters = options.parameters ?? {};
    const provider = options.provider ?? AIProvider.openai;
    const input = [];
    for (const message of messages) {
      if (message.role === "system") continue;
      if (message.role === "assistant") {
        const replay = OpenAIChatResponses.validatedReplay(message, provider);
        if (replay) {
          input.push(...replay);
          continue;
        }
      }
      if (message.role === "tool") {
        if (message.toolCallId) {
          input.push({
            type: "function_call_output",
            call_id: message.toolCallId,
            output: message.content
          });
        }
        continue;
      }
      const parts = [];
      if (message.content.length > 0) {
        if (message.role === "assistant") {
          parts.push({ type: "output_text", text: message.content, annotations: [] });
        } else {
          parts.push({ type: "input_text", text: message.content });
        }
      }
      if (message.role === "user") {
        for (const attachment of message.attachments ?? []) {
          const base64 = attachment.base64 !== void 0 && attachment.base64.length > 0 ? attachment.base64 : void 0;
          switch (attachment.type) {
            case "image": {
              const url = base64 !== void 0 ? `data:${attachment.mimeType ?? "image/jpeg"};base64,${base64}` : attachment.url;
              if (url !== void 0) {
                parts.push({ type: "input_image", image_url: url, detail: "high" });
              }
              break;
            }
            case "document":
              if (base64 !== void 0) {
                parts.push({
                  type: "input_file",
                  filename: attachment.name ?? "attachment.pdf",
                  file_data: `data:${attachment.mimeType ?? "application/pdf"};base64,${base64}`
                });
              } else if (attachment.url !== void 0) {
                parts.push({ type: "input_file", file_url: attachment.url });
              }
              break;
            case "video": {
              if (provider !== AIProvider.meta) break;
              const url = base64 !== void 0 ? `data:video/mp4;base64,${base64}` : attachment.url;
              if (url !== void 0) parts.push({ type: "input_video", video_url: url });
              break;
            }
            case "audio": {
              if (provider !== AIProvider.meta) break;
              const mime = attachment.mimeType;
              if (base64 === void 0 || mime === void 0 || !MUSE_AUDIO_MIME_TYPES.includes(mime)) {
                throw StreamingError.parseError(
                  "Muse audio requires inline MP3 or WAV data with a MIME type"
                );
              }
              parts.push({
                type: "input_audio",
                input_audio: { data: base64, format: mime.includes("wav") ? "wav" : "mp3" }
              });
              break;
            }
          }
        }
      }
      if (parts.length > 0) input.push({ role: message.role, content: parts });
      if (message.role === "assistant") {
        for (const call of message.toolCalls ?? []) {
          input.push({
            type: "function_call",
            call_id: call.id,
            name: call.name,
            arguments: call.arguments
          });
        }
      }
    }
    const body = {
      model,
      input,
      max_output_tokens: maxTokens,
      store: false,
      include: ["reasoning.encrypted_content"],
      stream: options.stream ?? false
    };
    const instructions = [
      ...options.system != null ? [options.system] : [],
      ...messages.filter((m) => m.role === "system").map((m) => m.content)
    ].filter((text) => text.length > 0).join("\n\n");
    if (instructions.length > 0) body["instructions"] = instructions;
    if (provider === AIProvider.openai && options.promptCacheKey !== void 0) body["prompt_cache_key"] = options.promptCacheKey;
    const rawEffort = parameters["reasoning_effort"];
    if (typeof rawEffort === "string" && isOpenAIReasoningEffort(rawEffort)) {
      const normalized = OpenAIResponsesRouting.normalizedReasoningEffort(rawEffort, model);
      if (normalized !== null) body["reasoning"] = { effort: normalized };
    }
    if (provider === AIProvider.meta) {
      delete body["reasoning"];
      if (typeof rawEffort === "string") {
        const effort = rawEffort === "none" ? "minimal" : rawEffort === "max" && model !== "muse-spark-1.3" ? "xhigh" : rawEffort;
        if (isMuseReasoningEffort(effort)) body["reasoning"] = { effort };
      }
    }
    const tools = options.tools;
    if (tools && tools.length > 0) {
      body["tools"] = tools.map((tool) => ({
        type: "function",
        name: tool.name,
        description: tool.description,
        parameters: ToolFormatters.toolParametersSchema(tool.parameters),
        strict: false
      }));
      const choice = options.toolChoice;
      if (choice) {
        switch (choice.type) {
          case "auto":
          case "none":
          case "required":
            body["tool_choice"] = choice.type;
            break;
          case "specific":
            body["tool_choice"] = { type: "function", name: choice.toolName };
            break;
        }
      }
    }
    return body;
  },
  /// Never replay stale output after text editing or history sanitization removed a tool call.
  validatedReplay(message, provider = AIProvider.openai) {
    const metadata = message.providerMetadata;
    if ((metadata?.responsesProvider ?? AIProvider.openai) !== provider) return null;
    const raw = metadata?.responsesOutputJSON;
    if (raw === void 0) return null;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    const output = asObjectArray2(parsed);
    if (!output || output.length === 0) return null;
    if (!output.every((item) => REPLAY_ITEM_TYPES.includes(asString(item["type"]) ?? ""))) {
      return null;
    }
    if (OpenAIChatResponses.text(output) !== message.content) return null;
    let calls;
    try {
      calls = OpenAIChatResponses.toolCalls(output);
    } catch {
      return null;
    }
    if (!toolCallsEqual(calls, message.toolCalls ?? [])) return null;
    return output;
  },
  text(output) {
    return output.filter((item) => item["type"] === "message").flatMap((item) => asObjectArray2(item["content"]) ?? []).map((part) => asString(part["text"]) ?? asString(part["refusal"]) ?? "").join("");
  },
  reasoning(output) {
    return output.filter((item) => item["type"] === "reasoning").flatMap((item) => asObjectArray2(item["summary"]) ?? []).flatMap((part) => {
      const text = asString(part["text"]);
      return text !== void 0 ? [text] : [];
    }).join("");
  },
  toolCalls(output) {
    const ids = /* @__PURE__ */ new Set();
    return output.filter((item) => item["type"] === "function_call").map((item) => {
      const id = asString(item["call_id"]);
      const name = asString(item["name"]);
      const args = asString(item["arguments"]);
      if (id === void 0 || id.length === 0 || name === void 0 || name.length === 0 || args === void 0 || ids.has(id)) {
        throw StreamingError.parseError("Invalid or duplicate Responses function call");
      }
      ids.add(id);
      return { id, name, arguments: args };
    });
  },
  completion(response, model, provider) {
    if (response["status"] !== "completed") {
      const error = asRecord(response["error"]);
      const details = asRecord(response["incomplete_details"]);
      throw StreamingError.providerError(
        200,
        asString(error?.["message"]) ?? asString(details?.["reason"]) ?? "Responses request did not complete"
      );
    }
    const output = asObjectArray2(response["output"]);
    if (!output) {
      throw StreamingError.parseError("Responses output is missing");
    }
    const calls = OpenAIChatResponses.toolCalls(output);
    const usage = asRecord(response["usage"]);
    const summary = OpenAIChatResponses.reasoning(output);
    const providerMetadata = {
      responsesOutputJSON: JSON.stringify(output),
      responsesProvider: provider === providerDisplayName(AIProvider.meta) ? "meta" : "openai"
    };
    return {
      fullContent: OpenAIChatResponses.text(output),
      reasoning: summary.length > 0 ? summary : void 0,
      tokens: usage !== void 0 ? ProviderPromptCaching.openAIUsage(usage) : void 0,
      modelName: asString(response["model"]) ?? model,
      providerName: provider,
      toolCalls: calls.length > 0 ? calls : void 0,
      stopReason: calls.length > 0 ? "tool_calls" : "stop",
      providerMetadata
    };
  }
};
var OpenAIResponsesStreamReducer = class {
  _completed = false;
  text = "";
  reasoning = "";
  calls = /* @__PURE__ */ new Map();
  started = /* @__PURE__ */ new Set();
  get completed() {
    return this._completed;
  }
  consume(data, model, provider) {
    if (this._completed) return [];
    if (data === "[DONE]") {
      throw StreamingError.parseError("Invalid or prematurely terminated Responses stream");
    }
    const parsed = JSON.parse(data);
    const event = isJSONObject(parsed) ? parsed : void 0;
    const type = asString(event?.["type"]);
    if (!event || type === void 0) {
      throw StreamingError.parseError("Invalid or prematurely terminated Responses stream");
    }
    switch (type) {
      case "response.output_text.delta":
      case "response.refusal.delta": {
        const delta = asString(event["delta"]) ?? "";
        this.text += delta;
        return [StreamingEvents.textDelta(delta)];
      }
      case "response.reasoning_summary_text.delta": {
        const delta = asString(event["delta"]) ?? "";
        this.reasoning += delta;
        return [StreamingEvents.reasoningDelta(delta)];
      }
      case "response.output_item.added":
      case "response.output_item.done": {
        const item = asRecord(event["item"]);
        const index = asInt(event["output_index"]);
        const id = asString(item?.["call_id"]);
        const name = asString(item?.["name"]);
        if (!item || item["type"] !== "function_call" || index === void 0 || id === void 0 || name === void 0) {
          return [];
        }
        this.calls.set(index, {
          id,
          name,
          arguments: asString(item["arguments"]) ?? this.calls.get(index)?.arguments ?? ""
        });
        if (this.started.has(id)) return [];
        this.started.add(id);
        return [StreamingEvents.toolCallStart({ id, name, arguments: "" })];
      }
      case "response.function_call_arguments.delta": {
        const index = asInt(event["output_index"]);
        const call = index !== void 0 ? this.calls.get(index) : void 0;
        const delta = asString(event["delta"]);
        if (index === void 0 || !call || delta === void 0) return [];
        this.calls.set(index, { id: call.id, name: call.name, arguments: call.arguments + delta });
        return [StreamingEvents.toolCallDelta(call.id, delta)];
      }
      case "response.completed": {
        const response = event["response"];
        if (!isJSONObject(response)) {
          throw StreamingError.parseError("Missing completed response");
        }
        const completion = OpenAIChatResponses.completion(response, model, provider);
        const summary = completion.reasoning ?? "";
        if (!completion.fullContent.startsWith(this.text) || !summary.startsWith(this.reasoning)) {
          throw StreamingError.parseError("Responses terminal output differs from streamed content");
        }
        const events = [];
        const suffix = completion.fullContent.slice(this.text.length);
        if (suffix.length > 0) events.push(StreamingEvents.textDelta(suffix));
        const summarySuffix = summary.slice(this.reasoning.length);
        if (summarySuffix.length > 0) events.push(StreamingEvents.reasoningDelta(summarySuffix));
        for (const call of completion.toolCalls ?? []) {
          if (!this.started.has(call.id)) {
            this.started.add(call.id);
            events.push(StreamingEvents.toolCallStart({ id: call.id, name: call.name, arguments: "" }));
          }
          events.push(StreamingEvents.toolCallComplete(call));
        }
        if (completion.toolCalls && completion.toolCalls.length > 0) {
          events.push(StreamingEvents.toolResultRequired(completion.toolCalls));
        }
        events.push(StreamingEvents.completion(completion));
        this._completed = true;
        return events;
      }
      case "response.failed":
      case "response.incomplete":
      case "error": {
        const response = asRecord(event["response"]) ?? event;
        const error = asRecord(response["error"]);
        const details = asRecord(response["incomplete_details"]);
        throw StreamingError.providerError(
          200,
          asString(error?.["message"]) ?? asString(event["message"]) ?? asString(details?.["reason"]) ?? type
        );
      }
      default:
        return [];
    }
  }
  finish() {
    if (!this._completed) {
      throw StreamingError.connectionFailed("Responses stream ended before response.completed");
    }
  }
};

// src/client/services/provider-request-support.ts
var ANTHROPIC_MODELS_WITHOUT_SAMPLING = [
  "claude-fable-5",
  "claude-fable-5-1",
  "claude-opus-5",
  "claude-opus-5-5",
  "claude-sonnet-5",
  "claude-sonnet-5-5",
  "claude-haiku-5-5",
  "claude-opus-4-7",
  "claude-opus-4-8"
];
var ALWAYS_REASONING_GLM_MODELS = ["glm-5.3", "glm-5.3-flash", "glm-5.3-flashx"];
var ProviderRequestSupport = {
  /// Claude Opus 5.5 rejects forced tool use (`required` / `specific`).
  validateAnthropicToolChoice(model, choice) {
    if (!["claude-opus-5-5", "claude-sonnet-5-5"].includes(model)) return;
    if (choice?.type === "required" || choice?.type === "specific") {
      throw StreamingError.parseError(
        `${model} does not support forced tool choice; use auto instead`
      );
    }
  },
  /// New Claude models reject legacy sampling controls (temperature/top_p/top_k).
  omitsAnthropicSampling(model) {
    return ANTHROPIC_MODELS_WITHOUT_SAMPLING.includes(model);
  },
  /**
   * GLM 5.3 always reasons. Map a saved disabled setting to its documented
   * low-effort equivalent, while leaving older GLM and custom providers alone.
   * Returns a new object; the input is never mutated.
   */
  zaiParameters(model, parameters) {
    if (!ALWAYS_REASONING_GLM_MODELS.includes(model)) return parameters;
    const result = { ...parameters };
    const thinking = result["thinking"];
    if (isJSONObject(thinking) && thinking["type"] === "disabled") {
      result["thinking"] = { type: "enabled" };
      result["reasoning_effort"] = "low";
    }
    const effort = result["reasoning_effort"];
    if (typeof effort === "string") {
      switch (effort) {
        case "none":
        case "minimal":
          result["reasoning_effort"] = "low";
          break;
        case "medium":
          result["reasoning_effort"] = "high";
          break;
        case "xhigh":
          result["reasoning_effort"] = "max";
          break;
      }
    }
    return result;
  },
  /**
   * The token-limit parameter a provider's chat completions endpoint expects.
   * OpenAI and MiniMax use `max_completion_tokens`; everyone else uses `max_tokens`.
   */
  chatCompletionsDefaultTokenLimit(provider) {
    switch (provider) {
      case AIProvider.openai:
      case AIProvider.minimax:
        return "max_completion_tokens";
      default:
        return "max_tokens";
    }
  },
  /// Trim whitespace/trailing slashes and strip endpoint path suffixes.
  normalizedBaseURL(baseUrl) {
    let normalizedUrl = baseUrl.trim();
    while (normalizedUrl.endsWith("/")) {
      normalizedUrl = normalizedUrl.slice(0, -1);
    }
    for (const path of ["/chat/completions", "/completions"]) {
      if (normalizedUrl.toLowerCase().endsWith(path)) {
        normalizedUrl = normalizedUrl.slice(0, -path.length);
        break;
      }
    }
    return normalizedUrl;
  },
  /// OpenAI-compatible messages array (delegates to the shared builder).
  openAICompatibleMessages(messages, system) {
    return buildOpenAICompatibleMessages(messages, system);
  },
  /// Assemble an OpenAI-compatible chat completions request body.
  openAICompatibleBody(options) {
    const tokenLimitParameter = options.tokenLimitParameter ?? "max_tokens";
    const body = {
      model: options.model,
      messages: buildOpenAICompatibleMessages(options.messages, options.system)
    };
    body[tokenLimitParameter] = options.maxTokens;
    if (options.stream != null) {
      body["stream"] = options.stream;
    }
    if (options.settings) {
      Object.assign(body, options.settings.toRequestParameters());
    }
    if (options.extraParams) {
      Object.assign(body, options.extraParams);
    }
    return body;
  },
  /// Assemble a MiniMax OpenAI-compatible request body (`max_completion_tokens`).
  miniMaxOpenAICompatibleBody(options) {
    const body = {
      model: options.model,
      messages: buildOpenAICompatibleMessages(options.messages, options.system),
      max_completion_tokens: options.maxTokens
    };
    if (options.settings) {
      Object.assign(body, options.settings.toRequestParameters());
    }
    return body;
  },
  /**
   * Decode an OpenAI-compatible chat completions response body, including
   * `reasoning_content` and Perplexity-style `citations`.
   */
  decodeOpenAICompatibleResponse(body) {
    let decoded;
    try {
      decoded = JSON.parse(body);
    } catch (error) {
      throw APIError.parseError(
        error instanceof Error ? error.message : "Invalid JSON response"
      );
    }
    if (!Array.isArray(decoded.choices)) {
      throw APIError.parseError("Missing choices in response");
    }
    const message = decoded.choices[0]?.message;
    const usage = decoded.usage;
    const tokens = usage != null ? ProviderPromptCaching.openAIUsage(usage) : void 0;
    const citations = Array.isArray(decoded.citations) ? decoded.citations.filter((c) => typeof c === "string") : null;
    const metadata = citations && citations.length > 0 ? { citations } : void 0;
    return {
      content: typeof message?.content === "string" ? message.content : "",
      reasoning: typeof message?.reasoning_content === "string" ? message.reasoning_content : void 0,
      tokens,
      providerMetadata: metadata
    };
  }
};

// src/client/services/provider-client.ts
var ProviderClient = class {
  accessPolicy;
  transport;
  customHandlers = /* @__PURE__ */ new Map();
  constructor(options = {}) {
    this.accessPolicy = options.accessPolicy ?? AIProviderAccessPolicy.all;
    this.transport = options.transport ?? defaultTransport;
  }
  // MARK: - Custom Handler Registration
  /// Register a custom provider handler.
  registerCustomHandler(handler) {
    this.customHandlers.set(handler.providerId, handler);
  }
  /// Unregister a custom provider handler.
  unregisterCustomHandler(providerId) {
    this.customHandlers.delete(providerId);
  }
  // MARK: - Send to Built-in Provider
  /// Send messages to a built-in AI provider and return the assistant message.
  async send(options) {
    const { provider, model, apiKey, messages } = options;
    const system = options.system ?? null;
    const maxTokens = options.maxTokens ?? 4096;
    const settings = options.settings;
    const signal = options.signal;
    this.validateAccess(provider, model);
    let response;
    let includeReasoning = false;
    switch (provider) {
      case AIProvider.anthropic:
        response = await this.callAnthropic({
          apiKey,
          model,
          messages,
          system,
          maxTokens,
          settings: settings instanceof AnthropicSettings ? settings : null,
          signal
        });
        break;
      case AIProvider.meta:
      case AIProvider.openai:
        if (provider === AIProvider.meta || OpenAIResponsesRouting.usesResponses(model)) {
          response = await this.callOpenAIResponses({
            apiKey,
            model,
            messages,
            system,
            maxTokens,
            settings,
            provider,
            signal
          });
        } else {
          response = await this.callOpenAICompatible({
            apiKey,
            baseUrl: "https://api.openai.com/v1",
            model,
            messages,
            system,
            maxTokens,
            settings: settings instanceof OpenAISettings ? settings : null,
            tokenLimitParameter: ProviderRequestSupport.chatCompletionsDefaultTokenLimit(
              AIProvider.openai
            ),
            signal
          });
        }
        includeReasoning = true;
        break;
      case AIProvider.gemini:
        response = await this.callGemini({
          apiKey,
          model,
          messages,
          system,
          maxTokens,
          settings: settings instanceof GeminiSettings ? settings : null,
          signal
        });
        break;
      case AIProvider.xai: {
        let openAISettings;
        let extraParams = null;
        if (settings instanceof GrokReasoningSettings) {
          openAISettings = settings.base;
          const extras = {};
          if (settings.reasoningEffort !== void 0) {
            extras["reasoning_effort"] = settings.reasoningEffort;
          }
          if (settings.useEncryptedContent !== void 0) {
            extras["use_encrypted_content"] = settings.useEncryptedContent;
          }
          extraParams = Object.keys(extras).length > 0 ? extras : null;
        } else {
          openAISettings = settings instanceof OpenAISettings ? settings : null;
        }
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.x.ai/v1",
          model,
          messages,
          system,
          maxTokens,
          settings: openAISettings,
          extraParams,
          signal
        });
        includeReasoning = true;
        break;
      }
      case AIProvider.perplexity: {
        const base = settings instanceof PerplexitySettings ? settings.base : settings instanceof OpenAISettings ? settings : null;
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.perplexity.ai",
          model,
          messages,
          system,
          maxTokens,
          settings: base,
          extraParams: settings instanceof PerplexitySettings ? settings.toPerplexityParams() : null,
          signal
        });
        break;
      }
      case AIProvider.deepseek:
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.deepseek.com",
          model,
          messages,
          system,
          maxTokens,
          settings: settings instanceof OpenAISettings ? settings : null,
          signal
        });
        includeReasoning = true;
        break;
      case AIProvider.zai: {
        let base = null;
        const extraParams = {};
        if (settings instanceof ZaiSettings) {
          base = settings.base;
          if (settings.thinkingEnabled !== void 0) {
            extraParams["thinking"] = {
              type: settings.thinkingEnabled ? "enabled" : "disabled"
            };
          }
        } else if (settings instanceof OpenAISettings) {
          base = settings;
        }
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.z.ai/api/paas/v4",
          model,
          messages,
          system,
          maxTokens,
          settings: null,
          extraParams: ProviderRequestSupport.zaiParameters(model, {
            ...base?.toRequestParameters() ?? {},
            ...extraParams
          }),
          signal
        });
        includeReasoning = true;
        break;
      }
      case AIProvider.minimax:
        response = await this.callMiniMax({
          apiKey,
          model,
          messages,
          system,
          maxTokens,
          settings: settings instanceof MiniMaxSettings ? settings : null,
          signal
        });
        break;
      case AIProvider.mistral:
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.mistral.ai/v1",
          model,
          messages,
          system,
          maxTokens,
          settings: settings instanceof OpenAISettings ? settings : null,
          signal
        });
        break;
      case AIProvider.venice: {
        let base = null;
        let extraParams = null;
        if (settings instanceof VeniceSettings) {
          base = settings.base;
          extraParams = settings.toVeniceParams();
        } else if (settings instanceof OpenAISettings) {
          base = settings;
        }
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl: "https://api.venice.ai/api/v1",
          model,
          messages,
          system,
          maxTokens,
          settings: base,
          extraParams,
          signal
        });
        break;
      }
      case AIProvider.openAICompatible: {
        const compatSettings = settings instanceof OpenAICompatibleSettings ? settings : null;
        const baseUrl = compatSettings?.baseUrl ?? "http://localhost:1234/v1";
        response = await this.callOpenAICompatible({
          apiKey,
          baseUrl,
          model,
          messages,
          system,
          maxTokens,
          settings: compatSettings?.base ?? (settings instanceof OpenAISettings ? settings : null),
          signal
        });
        includeReasoning = true;
        break;
      }
      case AIProvider.appleIntelligence:
        throw APIError.unsupportedProvider(
          `${providerDisplayName(provider)} requires an Apple platform runtime; register a CustomProviderHandler bridge`
        );
      case AIProvider.mlx:
        throw APIError.unsupportedProvider(
          'Use sendToHandler with customProviderId "localMLX" after registering an MLX handler'
        );
      case AIProvider.aiEdge:
        throw APIError.unsupportedProvider(
          "Use sendToHandler after registering an AI Edge handler"
        );
    }
    return createMessage({
      role: "assistant",
      content: response.content,
      ...response.tokens !== void 0 && { tokens: response.tokens },
      modelName: model,
      providerName: providerDisplayName(provider),
      ...includeReasoning && response.reasoning !== void 0 && { reasoning: response.reasoning },
      ...response.providerMetadata !== void 0 && {
        providerMetadata: response.providerMetadata
      }
    });
  }
  validateAccess(provider, model) {
    if (!this.accessPolicy.allowsProvider(provider)) {
      throw APIError.invalidRequest(
        `Provider ${providerDisplayName(provider)} is not allowed by this access policy`
      );
    }
    if (!this.accessPolicy.allowsModel(provider, model)) {
      throw APIError.invalidRequest(
        `Model ${model} is not allowed for provider ${providerDisplayName(provider)} by this access policy`
      );
    }
  }
  // MARK: - Send to Custom OpenAI-Compatible Provider
  /// Send messages to a custom OpenAI-compatible provider.
  async sendToCustomProvider(options) {
    const response = await this.callOpenAICompatible({
      apiKey: options.apiKey,
      baseUrl: options.baseUrl,
      model: options.model,
      messages: options.messages,
      system: options.system ?? null,
      maxTokens: options.maxTokens ?? 4096,
      settings: options.settings ?? null,
      signal: options.signal
    });
    return createMessage({
      role: "assistant",
      content: response.content,
      ...response.tokens !== void 0 && { tokens: response.tokens },
      modelName: options.model,
      providerName: "Custom",
      ...response.reasoning !== void 0 && { reasoning: response.reasoning },
      ...response.providerMetadata !== void 0 && {
        providerMetadata: response.providerMetadata
      }
    });
  }
  // MARK: - Send to Registered Custom Handler
  /// Send messages to a registered custom provider handler.
  async sendToHandler(options) {
    const handler = this.customHandlers.get(options.customProviderId);
    if (!handler) {
      throw APIError.unsupportedProvider(options.customProviderId);
    }
    const response = await handler.generate({
      messages: options.messages,
      model: options.model,
      system: options.system,
      maxTokens: options.maxTokens ?? 4096,
      settings: options.settings
    });
    return createMessage({
      role: "assistant",
      content: response.content,
      ...response.tokens !== void 0 && { tokens: response.tokens },
      modelName: options.model,
      providerName: options.customProviderId,
      ...response.reasoning !== void 0 && { reasoning: response.reasoning },
      ...response.toolCalls !== void 0 && { toolCalls: response.toolCalls },
      ...response.providerMetadata !== void 0 && {
        providerMetadata: response.providerMetadata
      }
    });
  }
  // MARK: - Anthropic
  async callAnthropic(options) {
    const apiMessages = [];
    for (const msg of options.messages) {
      if (msg.role === "system") continue;
      const role = msg.role === "assistant" ? "assistant" : "user";
      apiMessages.push({
        role,
        content: AnthropicContentReplay.validatedBlocks(msg) ?? anthropicBlocksWithFallback(msg)
      });
    }
    const body = {
      model: options.model,
      max_tokens: options.maxTokens,
      messages: ProviderPromptCaching.anthropicMessages(apiMessages)
    };
    if (options.system && options.system.length > 0) {
      body["system"] = ProviderPromptCaching.anthropicSystem(options.system);
    }
    if (options.settings && !ProviderRequestSupport.omitsAnthropicSampling(options.model)) {
      Object.assign(body, options.settings.toRequestParameters());
    }
    const response = await this.transport({
      url: "https://api.anthropic.com/v1/messages",
      method: "POST",
      headers: {
        "x-api-key": options.apiKey,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json"
      },
      body: JSON.stringify(body),
      signal: options.signal
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw ErrorResponseParser.makeError(response.status, responseBody);
    }
    let decoded;
    try {
      decoded = JSON.parse(responseBody);
    } catch {
      throw APIError.parseError("Invalid Anthropic response");
    }
    const blocks = decoded.content;
    if (!Array.isArray(blocks) || !blocks.every((block) => isJSONObject(block) && typeof block["type"] === "string")) {
      throw APIError.parseError("Invalid Anthropic response");
    }
    const contentBlocks = blocks;
    const content = contentBlocks.filter((block) => block["type"] === "text").flatMap((block) => typeof block["text"] === "string" ? [block["text"]] : []).join("");
    const usage = decoded.usage;
    const tokens = ProviderPromptCaching.anthropicUsage(usage);
    return {
      content,
      reasoning: void 0,
      tokens,
      providerMetadata: AnthropicContentReplay.metadata(contentBlocks)
    };
  }
  // MARK: - OpenAI Responses
  async callOpenAIResponses(options) {
    const { provider } = options;
    const body = OpenAIChatResponses.body({
      messages: options.messages,
      model: options.model,
      system: options.system,
      maxTokens: options.maxTokens,
      parameters: options.settings?.toRequestParameters() ?? {},
      provider
    });
    const response = await this.transport({
      url: `${providerBaseURL(provider)}/responses`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${options.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body),
      signal: options.signal
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw ErrorResponseParser.makeError(response.status, responseBody);
    }
    let json;
    try {
      json = JSON.parse(responseBody);
    } catch {
      json = void 0;
    }
    if (!isJSONObject(json)) {
      throw StreamingError.parseError("Invalid Responses response");
    }
    const result = OpenAIChatResponses.completion(
      json,
      options.model,
      provider === AIProvider.meta ? providerDisplayName(AIProvider.meta) : "OpenAI"
    );
    return {
      content: result.fullContent,
      reasoning: result.reasoning,
      tokens: result.tokens,
      providerMetadata: result.providerMetadata
    };
  }
  // MARK: - OpenAI Compatible
  async callOpenAICompatible(options) {
    const normalizedUrl = ProviderRequestSupport.normalizedBaseURL(options.baseUrl);
    const body = ProviderRequestSupport.openAICompatibleBody({
      messages: options.messages,
      model: options.model,
      system: options.system,
      maxTokens: options.maxTokens,
      settings: options.settings,
      extraParams: options.extraParams ?? null,
      tokenLimitParameter: options.tokenLimitParameter ?? "max_tokens"
    });
    const response = await this.transport({
      url: `${normalizedUrl}/chat/completions`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${options.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body),
      signal: options.signal
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw ErrorResponseParser.makeError(response.status, responseBody);
    }
    return ProviderRequestSupport.decodeOpenAICompatibleResponse(responseBody);
  }
  // MARK: - Gemini
  async callGemini(options) {
    const modelId = options.model.startsWith("models/") ? options.model : `models/${options.model}`;
    const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:generateContent?key=${options.apiKey}`;
    const contents = [];
    for (const msg of options.messages) {
      if (msg.role === "system") continue;
      const role = msg.role === "assistant" ? "model" : "user";
      contents.push({ role, parts: geminiParts(msg) });
    }
    const body = { contents };
    if (options.system && options.system.length > 0) {
      body["systemInstruction"] = { parts: [{ text: options.system }] };
    }
    const generationConfig = { maxOutputTokens: options.maxTokens };
    if (options.settings) {
      Object.assign(generationConfig, options.settings.toRequestParameters());
    }
    body["generationConfig"] = generationConfig;
    const response = await this.transport({
      url,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: options.signal
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw ErrorResponseParser.makeError(response.status, responseBody);
    }
    let decoded;
    try {
      decoded = JSON.parse(responseBody);
    } catch {
      throw APIError.parseError("Invalid Gemini response");
    }
    const parts = decoded.candidates?.[0]?.content?.parts ?? [];
    let content = "";
    let reasoning = "";
    for (const part of parts) {
      const text = typeof part.text === "string" ? part.text : "";
      if (part.thought === true) {
        reasoning += text;
      } else {
        content += text;
      }
    }
    const usage = decoded.usageMetadata;
    const inputTokens = typeof usage?.promptTokenCount === "number" ? usage.promptTokenCount : 0;
    const outputTokens = typeof usage?.candidatesTokenCount === "number" ? usage.candidatesTokenCount : 0;
    const tokens = usage != null ? {
      input: inputTokens,
      output: outputTokens,
      total: inputTokens + outputTokens,
      ...typeof usage.cachedContentTokenCount === "number" && { cachedInput: usage.cachedContentTokenCount }
    } : void 0;
    return {
      content,
      reasoning: reasoning.length > 0 ? reasoning : void 0,
      tokens,
      providerMetadata: void 0
    };
  }
  // MARK: - MiniMax
  async callMiniMax(options) {
    const body = ProviderRequestSupport.miniMaxOpenAICompatibleBody({
      messages: options.messages,
      model: options.model,
      system: options.system,
      maxTokens: options.maxTokens,
      settings: options.settings
    });
    const response = await this.transport({
      url: "https://api.minimax.io/v1/chat/completions",
      method: "POST",
      headers: {
        Authorization: `Bearer ${options.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body),
      signal: options.signal
    });
    const responseBody = await response.text();
    if (response.status !== 200) {
      throw ErrorResponseParser.makeError(response.status, responseBody);
    }
    return ProviderRequestSupport.decodeOpenAICompatibleResponse(responseBody);
  }
};
function anthropicBlocksWithFallback(message) {
  const blocks = [];
  const trimmedText = message.content.trim();
  if (trimmedText.length > 0) {
    blocks.push({ type: "text", text: trimmedText });
  }
  for (const attachment of message.attachments ?? []) {
    if (attachment.type !== "image" && attachment.type !== "document") continue;
    const mimeType = attachment.mimeType ?? (attachment.type === "image" ? "image/jpeg" : "application/pdf");
    if (attachment.base64) {
      blocks.push({
        type: attachment.type,
        source: { type: "base64", media_type: mimeType, data: attachment.base64 }
      });
    } else if (attachment.url) {
      blocks.push({
        type: attachment.type,
        source: { type: "url", url: attachment.url }
      });
    }
  }
  if (blocks.length === 0) {
    blocks.push({ type: "text", text: "" });
  }
  return blocks;
}

// src/client/services/sse-parser.ts
function tryParseObject(data) {
  try {
    const parsed = JSON.parse(data);
    return isJSONObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
function asRecord2(value) {
  return isJSONObject(value) ? value : null;
}
function base64ToUint8Array(base64) {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}
var SSEParser = {
  /**
   * Extract data content from an SSE line. Returns null if the line is not a
   * data line. `[DONE]` signals pass through for the caller to handle.
   */
  parseDataLine(line) {
    const trimmed = line.trim();
    if (trimmed.length === 0 || trimmed.startsWith(":")) {
      return null;
    }
    if (trimmed.startsWith("data:")) {
      const data = trimmed.slice(5).trim();
      if (data === "[DONE]") {
        return "[DONE]";
      }
      return data.length === 0 ? null : data;
    }
    return null;
  },
  /// Parse an Anthropic SSE data payload.
  parseAnthropicEvent(data) {
    const json = tryParseObject(data);
    if (!json || typeof json["type"] !== "string") return null;
    switch (json["type"]) {
      case "message_start":
        return { type: "messageStart", usage: decodeAnthropicUsage(asRecord2(asRecord2(json["message"])?.["usage"])) };
      case "content_block_start": {
        const index = typeof json["index"] === "number" ? json["index"] : 0;
        const contentBlock = asRecord2(json["content_block"]);
        if (contentBlock && contentBlock["type"] === "tool_use" && typeof contentBlock["id"] === "string" && typeof contentBlock["name"] === "string") {
          return {
            type: "toolUseStart",
            index,
            id: contentBlock["id"],
            name: contentBlock["name"]
          };
        }
        return { type: "contentBlockStart", index };
      }
      case "content_block_delta": {
        const index = typeof json["index"] === "number" ? json["index"] : 0;
        const delta = asRecord2(json["delta"]);
        if (delta) {
          const deltaType = typeof delta["type"] === "string" ? delta["type"] : "text_delta";
          if (deltaType === "text_delta" && typeof delta["text"] === "string") {
            return { type: "contentBlockDelta", index, text: delta["text"] };
          }
          if (deltaType === "input_json_delta" && typeof delta["partial_json"] === "string") {
            return { type: "toolUseDelta", index, partialJson: delta["partial_json"] };
          }
        }
        return null;
      }
      case "content_block_stop": {
        const index = typeof json["index"] === "number" ? json["index"] : 0;
        return { type: "contentBlockStop", index };
      }
      case "message_delta": {
        const delta = asRecord2(json["delta"]);
        const stopReason = delta && typeof delta["stop_reason"] === "string" ? delta["stop_reason"] : null;
        const usageDict = asRecord2(json["usage"]);
        const usage = decodeAnthropicUsage(usageDict);
        return { type: "messageDelta", stopReason, usage };
      }
      case "message_stop":
        return { type: "messageStop" };
      case "error": {
        const error = asRecord2(json["error"]);
        return {
          type: "error",
          errorType: typeof error?.["type"] === "string" ? error["type"] : "unknown",
          message: typeof error?.["message"] === "string" ? error["message"] : "Unknown error"
        };
      }
      default:
        return null;
    }
  },
  parseOpenAIUsage(data) {
    const usage = asRecord2(tryParseObject(data)?.["usage"]);
    return usage ? ProviderPromptCaching.openAIUsage(usage) : void 0;
  },
  /// Parse an OpenAI-compatible SSE data payload.
  parseOpenAIEvent(data) {
    if (data === "[DONE]") {
      return { type: "done" };
    }
    const json = tryParseObject(data);
    const choices = json?.["choices"];
    if (!Array.isArray(choices) || choices.length === 0) return null;
    const firstChoice = asRecord2(choices[0]);
    if (!firstChoice) return null;
    const finishReason = typeof firstChoice["finish_reason"] === "string" ? firstChoice["finish_reason"] : null;
    const delta = asRecord2(firstChoice["delta"]);
    if (delta) {
      const content = typeof delta["content"] === "string" ? delta["content"] : null;
      const role = typeof delta["role"] === "string" ? delta["role"] : null;
      const reasoningContent = typeof delta["reasoning_content"] === "string" ? delta["reasoning_content"] : null;
      let toolCalls = null;
      const toolCallsArray = delta["tool_calls"];
      if (Array.isArray(toolCallsArray)) {
        toolCalls = toolCallsArray.flatMap((tc) => {
          const record = asRecord2(tc);
          if (!record || typeof record["index"] !== "number") return [];
          const fn = asRecord2(record["function"]);
          return [
            {
              index: record["index"],
              id: typeof record["id"] === "string" ? record["id"] : null,
              functionName: typeof fn?.["name"] === "string" ? fn["name"] : null,
              argumentsDelta: typeof fn?.["arguments"] === "string" ? fn["arguments"] : null
            }
          ];
        });
      }
      return { type: "delta", content, role, finishReason, toolCalls, reasoningContent };
    }
    return {
      type: "delta",
      content: null,
      role: null,
      finishReason,
      toolCalls: null,
      reasoningContent: null
    };
  },
  /**
   * Parse a Gemini JSON chunk (one object of the array-framed stream). Gemini
   * can return multiple events in a single chunk.
   */
  parseGeminiChunk(chunk) {
    const events = [];
    let cleaned = chunk.trim();
    if (cleaned.startsWith("[")) cleaned = cleaned.slice(1);
    if (cleaned.endsWith("]")) cleaned = cleaned.slice(0, -1);
    if (cleaned.startsWith(",")) cleaned = cleaned.slice(1);
    cleaned = cleaned.trim();
    if (cleaned.length === 0) return events;
    const json = tryParseObject(cleaned);
    if (!json) return events;
    const candidates = json["candidates"];
    if (Array.isArray(candidates)) {
      for (const candidateValue of candidates) {
        const candidate = asRecord2(candidateValue);
        if (!candidate) continue;
        const content = asRecord2(candidate["content"]);
        const parts = content?.["parts"];
        if (Array.isArray(parts)) {
          for (const partValue of parts) {
            const part = asRecord2(partValue);
            if (!part) continue;
            const isThought = part["thought"] === true;
            if (typeof part["text"] === "string") {
              events.push(
                isThought ? { type: "reasoningDelta", text: part["text"] } : { type: "textDelta", text: part["text"] }
              );
            }
            for (const [dataKey, mimeKey] of [
              ["inlineData", "mimeType"],
              ["inline_data", "mime_type"]
            ]) {
              const inlineData = asRecord2(part[dataKey]);
              if (inlineData && typeof inlineData[mimeKey] === "string" && typeof inlineData["data"] === "string") {
                const bytes = base64ToUint8Array(inlineData["data"]);
                if (bytes) {
                  events.push({
                    type: "imageDelta",
                    data: bytes,
                    mimeType: inlineData[mimeKey]
                  });
                }
              }
            }
            const functionCall = asRecord2(part["functionCall"]);
            if (functionCall && typeof functionCall["name"] === "string" && isJSONObject(functionCall["args"])) {
              const thoughtSignature = typeof part["thoughtSignature"] === "string" ? part["thoughtSignature"] : typeof part["thought_signature"] === "string" ? part["thought_signature"] : null;
              events.push({
                type: "functionCall",
                name: functionCall["name"],
                args: functionCall["args"],
                thoughtSignature
              });
            }
          }
        }
        if (typeof candidate["finishReason"] === "string") {
          events.push({ type: "finishReason", reason: candidate["finishReason"] });
        }
      }
    }
    const usageMetadata = asRecord2(json["usageMetadata"]);
    if (usageMetadata) {
      events.push({
        type: "usageMetadata",
        cachedTokens: typeof usageMetadata["cachedContentTokenCount"] === "number" ? usageMetadata["cachedContentTokenCount"] : void 0,
        promptTokens: typeof usageMetadata["promptTokenCount"] === "number" ? usageMetadata["promptTokenCount"] : 0,
        candidatesTokens: typeof usageMetadata["candidatesTokenCount"] === "number" ? usageMetadata["candidatesTokenCount"] : 0,
        totalTokens: typeof usageMetadata["totalTokenCount"] === "number" ? usageMetadata["totalTokenCount"] : 0
      });
    }
    return events;
  },
  /// Parse reasoning model SSE data (DeepSeek format).
  parseReasoningModelEvent(data) {
    if (data === "[DONE]") {
      return { type: "done" };
    }
    const json = tryParseObject(data);
    const choices = json?.["choices"];
    if (!Array.isArray(choices) || choices.length === 0) return null;
    const delta = asRecord2(asRecord2(choices[0])?.["delta"]);
    if (!delta) return null;
    if (typeof delta["reasoning_content"] === "string" && delta["reasoning_content"].length > 0) {
      return { type: "reasoningDelta", text: delta["reasoning_content"] };
    }
    if (typeof delta["content"] === "string" && delta["content"].length > 0) {
      return { type: "contentDelta", text: delta["content"] };
    }
    return null;
  }
};
var SSELineBuffer = class {
  buffer = "";
  decoder = new TextDecoder("utf-8");
  /// Append a chunk of bytes and return the complete lines it produced.
  append(chunk) {
    this.buffer += this.decoder.decode(chunk, { stream: true });
    const lines = [];
    let newlineIndex = this.buffer.indexOf("\n");
    while (newlineIndex !== -1) {
      lines.push(this.buffer.slice(0, newlineIndex));
      this.buffer = this.buffer.slice(newlineIndex + 1);
      newlineIndex = this.buffer.indexOf("\n");
    }
    return lines;
  }
  /// Flush any remaining content in the buffer (call at stream end).
  flush() {
    this.buffer += this.decoder.decode();
    if (this.buffer.length === 0) return null;
    const remaining = this.buffer;
    this.buffer = "";
    return remaining;
  }
};
function decodeAnthropicUsage(usage) {
  if (!usage) return null;
  const field = (key) => typeof usage[key] === "number" ? usage[key] : null;
  return {
    inputTokens: field("input_tokens"),
    outputTokens: field("output_tokens"),
    cacheReadInputTokens: field("cache_read_input_tokens"),
    cacheCreationInputTokens: field("cache_creation_input_tokens")
  };
}
function anthropicTokenUsage(start, delta) {
  const raw = (u) => u ? {
    input_tokens: u.inputTokens,
    output_tokens: u.outputTokens,
    cache_read_input_tokens: u.cacheReadInputTokens,
    cache_creation_input_tokens: u.cacheCreationInputTokens
  } : void 0;
  return ProviderPromptCaching.anthropicUsage(raw(start), raw(delta));
}

// src/client/services/streamed-json-accumulator.ts
var OPEN_BRACE = 123;
var CLOSE_BRACE = 125;
var QUOTE = 34;
var BACKSLASH = 92;
var StreamedJSONObjectAccumulator = class {
  byteBuffer = [];
  braceDepth = 0;
  inString = false;
  escapeNext = false;
  maxBufferBytes;
  decoder = new TextDecoder("utf-8");
  constructor(maxBufferBytes = 8 * 1024 * 1024) {
    this.maxBufferBytes = maxBufferBytes;
  }
  /**
   * Feeds one byte. Returns the UTF-8-decoded buffer (including any framing
   * bytes such as a leading `[` or `,`) when a top-level JSON object
   * completes, and resets the buffer for the next object.
   * Throws `StreamingError.parseError` if the buffer grows past
   * `maxBufferBytes` without completing an object.
   */
  consume(byte) {
    this.byteBuffer.push(byte);
    if (this.byteBuffer.length > this.maxBufferBytes) {
      throw StreamingError.parseError(
        `Streamed JSON exceeded ${this.maxBufferBytes} bytes without completing an object`
      );
    }
    if (this.escapeNext) {
      this.escapeNext = false;
      return null;
    }
    if (byte === BACKSLASH && this.inString) {
      this.escapeNext = true;
      return null;
    }
    if (byte === QUOTE) {
      this.inString = !this.inString;
      return null;
    }
    if (this.inString) return null;
    if (byte === OPEN_BRACE) {
      this.braceDepth += 1;
    } else if (byte === CLOSE_BRACE) {
      this.braceDepth -= 1;
      if (this.braceDepth === 0) {
        const completed = this.decoder.decode(new Uint8Array(this.byteBuffer));
        this.byteBuffer = [];
        return completed;
      }
    }
    return null;
  }
  /// Feed a whole chunk; returns every object completed within it.
  consumeChunk(chunk) {
    const completed = [];
    for (const byte of chunk) {
      const object = this.consume(byte);
      if (object !== null) {
        completed.push(object);
      }
    }
    return completed;
  }
};
function extractJsonObject(buffer) {
  let depth = 0;
  let inString = false;
  let startIndex = null;
  for (let i = buffer.length - 1; i >= 0; i--) {
    const char = buffer[i];
    if (i > 0 && buffer[i - 1] === "\\" && char !== "\\") {
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (!inString) {
      if (char === "}") {
        depth += 1;
      } else if (char === "{") {
        depth -= 1;
        if (depth === 0) {
          startIndex = i;
          break;
        }
      }
    }
  }
  if (startIndex === null) return null;
  return buffer.slice(startIndex);
}

// src/client/services/streaming-client.ts
var StreamingClient = class {
  accessPolicy;
  transport;
  customHandlers = /* @__PURE__ */ new Map();
  constructor(options = {}) {
    this.accessPolicy = options.accessPolicy ?? AIProviderAccessPolicy.all;
    this.transport = options.transport ?? defaultTransport;
  }
  // MARK: - Custom Handler Registration
  /// Register a custom provider handler.
  registerCustomHandler(handler) {
    this.customHandlers.set(handler.providerId, handler);
  }
  /// Unregister a custom provider handler.
  unregisterCustomHandler(providerId) {
    this.customHandlers.delete(providerId);
  }
  // MARK: - Stream from Built-in Provider
  /// Stream responses from a built-in AI provider.
  async *stream(options) {
    const { provider, model } = options;
    const validationError = this.validateAccess(provider, model);
    if (validationError) {
      yield StreamingEvents.error(validationError);
      return;
    }
    if (provider === AIProvider.appleIntelligence) {
      const handler = this.customHandlers.get("appleFoundation");
      if (handler) {
        yield* this.streamFromHandler({
          messages: options.messages,
          customProviderId: "appleFoundation",
          model,
          system: options.system,
          maxTokens: options.maxTokens,
          settings: options.settings,
          tools: options.tools,
          toolChoice: options.toolChoice,
          signal: options.signal
        });
        return;
      }
      yield StreamingEvents.error(
        StreamingError.unsupportedProvider(
          `${providerDisplayName(provider)} requires an Apple platform runtime; register a CustomProviderHandler bridge`
        )
      );
      return;
    }
    if (!providerSupportsStreaming(provider)) {
      yield StreamingEvents.error(
        StreamingError.unsupportedProvider(
          `${providerDisplayName(provider)} does not support streaming in AxonProviderKit`
        )
      );
      return;
    }
    let baseUrl;
    let useStreaming;
    if (provider === AIProvider.openAICompatible && options.settings instanceof OpenAICompatibleSettings) {
      baseUrl = options.settings.baseUrl;
      useStreaming = options.settings.useStreaming;
    } else {
      baseUrl = providerBaseURL(provider);
      useStreaming = true;
    }
    const config = createStreamingConfig({
      provider,
      apiKey: options.apiKey,
      model,
      baseUrl,
      ...options.system !== void 0 && { system: options.system },
      ...options.maxTokens !== void 0 && { maxTokens: options.maxTokens },
      ...options.settings !== void 0 && { settings: options.settings },
      ...options.geminiCapabilities !== void 0 && {
        geminiCapabilities: options.geminiCapabilities
      },
      ...options.tools !== void 0 && { tools: options.tools },
      ...options.toolChoice !== void 0 && { toolChoice: options.toolChoice },
      ...options.promptCacheKey !== void 0 && { promptCacheKey: options.promptCacheKey },
      useStreaming
    });
    yield* this.streamWithConfig(
      config,
      options.messages,
      providerDisplayName(provider),
      options.signal
    );
  }
  validateAccess(provider, model) {
    if (!this.accessPolicy.allowsProvider(provider)) {
      return StreamingError.unsupportedProvider(
        `Provider ${providerDisplayName(provider)} is not allowed by this access policy`
      );
    }
    if (!this.accessPolicy.allowsModel(provider, model)) {
      return StreamingError.unsupportedProvider(
        `Model ${model} is not allowed for provider ${providerDisplayName(provider)} by this access policy`
      );
    }
    return null;
  }
  // MARK: - Stream from Custom Provider
  /// Stream responses from a custom OpenAI-compatible provider.
  async *streamFromCustomProvider(options) {
    const config = createStreamingConfig({
      provider: "openai-compatible",
      apiKey: options.apiKey,
      model: options.model,
      baseUrl: options.baseUrl,
      ...options.system !== void 0 && { system: options.system },
      ...options.maxTokens !== void 0 && { maxTokens: options.maxTokens },
      ...options.settings !== void 0 && { settings: options.settings }
    });
    yield* this.streamWithConfig(config, options.messages, "Custom", options.signal);
  }
  // MARK: - Stream from Registered Handler
  /// Stream responses from a registered custom handler.
  async *streamFromHandler(options) {
    const handler = this.customHandlers.get(options.customProviderId);
    if (!handler) {
      yield StreamingEvents.error(
        StreamingError.unsupportedProvider(options.customProviderId)
      );
      return;
    }
    yield* streamWithHandler(handler, {
      messages: options.messages,
      model: options.model,
      system: options.system,
      maxTokens: options.maxTokens ?? 4096,
      settings: options.settings,
      tools: options.tools,
      toolChoice: options.toolChoice,
      signal: options.signal
    });
  }
  // MARK: - Internal Streaming
  async *streamWithConfig(config, messages, providerName, signal) {
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    signal?.addEventListener("abort", onAbort, { once: true });
    try {
      switch (config.provider.toLowerCase()) {
        case "anthropic":
          yield* this.streamAnthropic(config, messages, providerName, controller.signal);
          break;
        case "meta":
          yield* this.streamOpenAIResponses(config, messages, providerName, controller.signal);
          break;
        case "openai":
          if (OpenAIResponsesRouting.usesResponses(config.model)) {
            yield* this.streamOpenAIResponses(config, messages, providerName, controller.signal);
            break;
          }
          yield* this.streamOpenAICompatibleAt(
            "https://api.openai.com/v1",
            config,
            messages,
            providerName,
            controller.signal
          );
          break;
        case "openai-compatible":
          yield* this.streamOpenAICompatible(config, messages, providerName, controller.signal);
          break;
        case "gemini":
          yield* this.streamGemini(config, messages, providerName, controller.signal);
          break;
        case "deepseek":
          yield* this.streamDeepSeek(config, messages, providerName, controller.signal);
          break;
        case "xai":
          yield* this.streamOpenAICompatibleAt(
            "https://api.x.ai/v1",
            config,
            messages,
            providerName,
            controller.signal
          );
          break;
        case "minimax":
          yield* this.streamOpenAICompatibleAt(
            "https://api.minimax.io/v1",
            config,
            messages,
            providerName,
            controller.signal
          );
          break;
        case "perplexity":
        case "zai":
        case "mistral":
        case "venice":
        case "openaicompatible":
          yield* this.streamOpenAICompatible(config, messages, providerName, controller.signal);
          break;
        default:
          yield StreamingEvents.error(StreamingError.unsupportedProvider(config.provider));
      }
    } catch (error) {
      const streamingError = error instanceof StreamingError ? error : StreamingError.connectionFailed(
        error instanceof Error ? error.message : String(error)
      );
      yield StreamingEvents.error(streamingError);
      throw error;
    } finally {
      signal?.removeEventListener("abort", onAbort);
      controller.abort();
    }
  }
  /// Iterate an SSE response body as decoded lines.
  async *lines(response) {
    const body = response.body;
    if (!body) {
      throw StreamingError.connectionFailed("Empty response body");
    }
    const reader = body.getReader();
    const buffer = new SSELineBuffer();
    try {
      for (; ; ) {
        const { done, value } = await reader.read();
        if (done) break;
        yield* buffer.append(value);
      }
      const rest = buffer.flush();
      if (rest !== null) {
        yield rest;
      }
    } finally {
      await reader.cancel().catch(() => {
      });
    }
  }
  /// Iterate a response body as raw byte chunks.
  async *byteChunks(response) {
    const body = response.body;
    if (!body) {
      throw StreamingError.connectionFailed("Empty response body");
    }
    const reader = body.getReader();
    try {
      for (; ; ) {
        const { done, value } = await reader.read();
        if (done) break;
        yield value;
      }
    } finally {
      await reader.cancel().catch(() => {
      });
    }
  }
  async throwProviderError(response, fallback) {
    const errorBody = await response.text().catch(() => "");
    const errorMessage = ErrorResponseParser.parseErrorBody(errorBody) ?? fallback;
    throw StreamingError.providerError(response.status, errorMessage);
  }
  // MARK: - Anthropic Streaming
  async *streamAnthropic(config, messages, providerName, signal) {
    const apiMessages = ProviderPromptCaching.anthropicMessages(buildAnthropicMessages(messages));
    const body = {
      model: config.model,
      max_tokens: config.maxTokens,
      stream: true,
      messages: apiMessages
    };
    if (config.system && config.system.length > 0) {
      body["system"] = ProviderPromptCaching.anthropicSystem(config.system);
    }
    if (config.tools && config.tools.length > 0) {
      ProviderRequestSupport.validateAnthropicToolChoice(config.model, config.toolChoice);
      body["tools"] = ProviderPromptCaching.anthropicTools(ToolFormatters.anthropicTools(config.tools));
      const toolChoice = ToolFormatters.anthropicToolChoice(config.toolChoice);
      if (toolChoice !== null) {
        body["tool_choice"] = toolChoice;
      }
    }
    if (config.settings && !ProviderRequestSupport.omitsAnthropicSampling(config.model)) {
      const settings = config.settings.toRequestParameters();
      for (const key of ["temperature", "top_p", "top_k"]) {
        if (settings[key] !== void 0) {
          body[key] = settings[key];
        }
      }
    }
    const response = await this.transport({
      url: "https://api.anthropic.com/v1/messages",
      method: "POST",
      headers: {
        "x-api-key": config.apiKey,
        "anthropic-version": "2023-06-01",
        "content-type": "application/json"
      },
      body: JSON.stringify(body),
      signal
    });
    if (response.status !== 200) {
      await this.throwProviderError(response, "Anthropic API error");
    }
    let accumulatedContent = "";
    let startUsage = null;
    let deltaUsage = null;
    let lastStopReason;
    const contentReplay = new AnthropicContentReplayAccumulator();
    const pendingToolCalls = /* @__PURE__ */ new Map();
    const completedToolCalls = [];
    for await (const line of this.lines(response)) {
      const data = SSEParser.parseDataLine(line);
      if (data === null) continue;
      contentReplay.consume(data);
      const event = SSEParser.parseAnthropicEvent(data);
      if (!event) continue;
      switch (event.type) {
        case "contentBlockDelta":
          accumulatedContent += event.text;
          yield StreamingEvents.textDelta(event.text);
          break;
        case "toolUseStart": {
          pendingToolCalls.set(event.index, {
            index: event.index,
            id: event.id,
            name: event.name,
            arguments: ""
          });
          yield StreamingEvents.toolCallStart({ id: event.id, name: event.name, arguments: "" });
          break;
        }
        case "toolUseDelta": {
          const partial = pendingToolCalls.get(event.index);
          if (partial) {
            partial.arguments += event.partialJson;
            yield StreamingEvents.toolCallDelta(partial.id, event.partialJson);
          }
          break;
        }
        case "contentBlockStop": {
          const partial = pendingToolCalls.get(event.index);
          if (partial) {
            pendingToolCalls.delete(event.index);
            const toolCall = partialToToolCall(partial);
            completedToolCalls.push(toolCall);
            yield StreamingEvents.toolCallComplete(toolCall);
          }
          break;
        }
        case "messageStart":
          startUsage = event.usage;
          break;
        case "messageDelta":
          if (event.usage) deltaUsage = event.usage;
          lastStopReason = event.stopReason ?? void 0;
          break;
        case "messageStop":
          if (lastStopReason === "tool_use" && completedToolCalls.length > 0) {
            yield StreamingEvents.toolResultRequired(completedToolCalls);
          }
          yield StreamingEvents.completion({
            fullContent: accumulatedContent,
            reasoning: void 0,
            tokens: anthropicTokenUsage(startUsage, deltaUsage),
            modelName: config.model,
            providerName,
            toolCalls: completedToolCalls.length > 0 ? completedToolCalls : void 0,
            stopReason: lastStopReason,
            providerMetadata: contentReplay.metadata
          });
          return;
        case "error":
          throw StreamingError.providerError(0, `${event.errorType}: ${event.message}`);
      }
    }
  }
  // MARK: - OpenAI Responses Streaming (OpenAI + Meta Muse)
  async *streamOpenAIResponses(config, messages, providerName, signal) {
    const provider = config.provider.toLowerCase() === "meta" ? AIProvider.meta : AIProvider.openai;
    const body = OpenAIChatResponses.body({
      messages,
      model: config.model,
      system: config.system,
      maxTokens: config.maxTokens,
      parameters: config.settings?.toRequestParameters() ?? {},
      tools: config.tools,
      toolChoice: config.toolChoice,
      stream: config.useStreaming,
      provider,
      promptCacheKey: config.promptCacheKey
    });
    try {
      const reducer = new OpenAIResponsesStreamReducer();
      const response = await this.transport({
        url: `${providerBaseURL(provider)}/responses`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${config.apiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body),
        signal
      });
      if (!config.useStreaming) {
        const responseBody = await response.text();
        if (response.status !== 200) {
          throw StreamingError.providerError(
            response.status,
            ErrorResponseParser.parseErrorBody(responseBody) ?? `${providerName} Responses API error`
          );
        }
        const responseObject = JSON.parse(responseBody);
        const event = JSON.stringify({ type: "response.completed", response: responseObject });
        yield* reducer.consume(event, config.model, providerName);
      } else {
        if (response.status !== 200) {
          await this.throwProviderError(response, `${providerName} Responses API error`);
        }
        for await (const line of this.lines(response)) {
          if (signal.aborted) throw StreamingError.cancelled();
          const data = SSEParser.parseDataLine(line);
          if (data === null) continue;
          yield* reducer.consume(data, config.model, providerName);
          if (reducer.completed) break;
        }
      }
      if (signal.aborted) throw StreamingError.cancelled();
      reducer.finish();
    } catch (error) {
      throw responsesTransportError(error, signal);
    }
  }
  // MARK: - OpenAI-Compatible Streaming
  /// Delegate to the compatible streamer with a fixed base URL (OpenAI/Grok/MiniMax).
  async *streamOpenAICompatibleAt(baseUrl, config, messages, providerName, signal) {
    const modified = createStreamingConfig({
      provider: config.provider,
      apiKey: config.apiKey,
      model: config.model,
      baseUrl,
      ...config.system !== void 0 && { system: config.system },
      maxTokens: config.maxTokens,
      ...config.settings !== void 0 && { settings: config.settings },
      ...config.tools !== void 0 && { tools: config.tools },
      ...config.toolChoice !== void 0 && { toolChoice: config.toolChoice },
      ...config.promptCacheKey !== void 0 && { promptCacheKey: config.promptCacheKey },
      useStreaming: config.useStreaming
    });
    yield* this.streamOpenAICompatible(modified, messages, providerName, signal);
  }
  async *streamOpenAICompatible(config, messages, providerName, signal) {
    const baseUrl = ProviderRequestSupport.normalizedBaseURL(
      config.baseUrl ?? "https://api.openai.com/v1"
    );
    const body = ProviderRequestSupport.openAICompatibleBody({
      messages,
      model: config.model,
      system: config.system,
      maxTokens: config.maxTokens,
      stream: config.useStreaming,
      tokenLimitParameter: ProviderRequestSupport.chatCompletionsDefaultTokenLimit(
        isAIProvider(config.provider) ? config.provider : AIProvider.openAICompatible
      )
    });
    if (config.tools && config.tools.length > 0) {
      body["tools"] = ToolFormatters.openAITools(config.tools);
      const toolChoice = ToolFormatters.openAIToolChoice(config.toolChoice);
      if (toolChoice !== null) {
        body["tool_choice"] = toolChoice;
      }
    }
    if (config.settings) {
      Object.assign(body, config.settings.toRequestParameters());
    }
    const requestBody = config.provider === AIProvider.zai ? ProviderRequestSupport.zaiParameters(config.model, body) : body;
    const reportsStreamUsage = config.useStreaming && [AIProvider.openai, AIProvider.xai].some((p) => p === config.provider);
    if (reportsStreamUsage) requestBody["stream_options"] = { include_usage: true };
    if (config.provider === AIProvider.openai && config.promptCacheKey !== void 0) {
      requestBody["prompt_cache_key"] = config.promptCacheKey;
    }
    const response = await this.transport({
      url: `${baseUrl}/chat/completions`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${config.apiKey}`,
        "Content-Type": "application/json",
        ...config.provider === AIProvider.xai && config.promptCacheKey !== void 0 && { "x-grok-conv-id": config.promptCacheKey }
      },
      body: JSON.stringify(requestBody),
      signal
    });
    if (!config.useStreaming) {
      const responseBody = await response.text();
      if (response.status !== 200) {
        const errorMessage = ErrorResponseParser.parseErrorBody(responseBody) ?? "OpenAI-compatible API error";
        throw StreamingError.providerError(response.status, errorMessage);
      }
      let json;
      try {
        json = JSON.parse(responseBody);
      } catch {
        throw StreamingError.connectionFailed("Invalid response format");
      }
      const choices = isJSONObject(json) ? json["choices"] : null;
      const firstChoice = Array.isArray(choices) && isJSONObject(choices[0]) ? choices[0] : null;
      const message = firstChoice && isJSONObject(firstChoice["message"]) ? firstChoice["message"] : null;
      if (!message) {
        throw StreamingError.connectionFailed("Invalid response format");
      }
      const content = typeof message["content"] === "string" ? message["content"] : "";
      const reasoning = typeof message["reasoning_content"] === "string" ? message["reasoning_content"] : null;
      if (content.length > 0) {
        yield StreamingEvents.textDelta(content);
      }
      if (reasoning && reasoning.length > 0) {
        yield StreamingEvents.reasoningDelta(reasoning);
      }
      yield StreamingEvents.completion({
        fullContent: content,
        reasoning: reasoning ?? void 0,
        tokens: isJSONObject(json) && isJSONObject(json["usage"]) ? ProviderPromptCaching.openAIUsage(json["usage"]) : void 0,
        modelName: config.model,
        providerName,
        toolCalls: void 0,
        stopReason: firstChoice && typeof firstChoice["finish_reason"] === "string" ? firstChoice["finish_reason"] : void 0
      });
      return;
    }
    if (response.status !== 200) {
      await this.throwProviderError(response, "OpenAI-compatible API error");
    }
    let accumulatedContent = "";
    let accumulatedReasoning = "";
    const pendingToolCalls = /* @__PURE__ */ new Map();
    const completedToolCalls = [];
    let pendingFinishReason;
    function* finish(reason, tokens) {
      if (reason === "tool_calls" && completedToolCalls.length > 0) {
        yield StreamingEvents.toolResultRequired(completedToolCalls);
      }
      yield StreamingEvents.completion({
        fullContent: accumulatedContent,
        reasoning: accumulatedReasoning || void 0,
        tokens,
        modelName: config.model,
        providerName,
        toolCalls: completedToolCalls.length ? completedToolCalls : void 0,
        stopReason: reason
      });
    }
    for await (const line of this.lines(response)) {
      const data = SSEParser.parseDataLine(line);
      if (data === null) continue;
      if (pendingFinishReason !== void 0) {
        const usage = SSEParser.parseOpenAIUsage(data);
        if (usage) {
          yield* finish(pendingFinishReason, usage);
          return;
        }
        if (data === "[DONE]") break;
        continue;
      }
      const event = SSEParser.parseOpenAIEvent(data);
      if (!event) continue;
      if (event.type === "done") {
        return;
      }
      if (event.reasoningContent && event.reasoningContent.length > 0) {
        accumulatedReasoning += event.reasoningContent;
        yield StreamingEvents.reasoningDelta(event.reasoningContent);
      }
      if (event.content !== null) {
        accumulatedContent += event.content;
        yield StreamingEvents.textDelta(event.content);
      }
      if (event.toolCalls) {
        for (const delta of event.toolCalls) {
          const existing = pendingToolCalls.get(delta.index);
          if (existing) {
            if (delta.id && delta.id.length > 0) {
              existing.id = delta.id;
            }
            if (delta.functionName && delta.functionName.length > 0) {
              existing.name = delta.functionName;
            }
            if (delta.argumentsDelta !== null) {
              existing.arguments += delta.argumentsDelta;
              yield StreamingEvents.toolCallDelta(existing.id, delta.argumentsDelta);
            }
          } else {
            const partial = createPartialToolCall(delta.index);
            partial.id = delta.id ?? "";
            partial.name = delta.functionName ?? "";
            partial.arguments = delta.argumentsDelta ?? "";
            pendingToolCalls.set(delta.index, partial);
            if (partial.id.length > 0 && partial.name.length > 0) {
              yield StreamingEvents.toolCallStart({
                id: partial.id,
                name: partial.name,
                arguments: ""
              });
            }
          }
        }
      }
      if (event.finishReason !== null) {
        for (const [, partial] of [...pendingToolCalls.entries()].sort((a, b) => a[0] - b[0])) {
          const toolCall = partialToToolCall(partial);
          completedToolCalls.push(toolCall);
          yield StreamingEvents.toolCallComplete(toolCall);
        }
        pendingToolCalls.clear();
        const usage = SSEParser.parseOpenAIUsage(data);
        if (!reportsStreamUsage || usage) {
          yield* finish(event.finishReason, usage);
          return;
        }
        pendingFinishReason = event.finishReason;
      }
    }
    if (pendingFinishReason !== void 0) yield* finish(pendingFinishReason);
  }
  // MARK: - Gemini Streaming
  async *streamGemini(config, messages, providerName, signal) {
    const modelId = config.model.startsWith("models/") ? config.model : `models/${config.model}`;
    const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:streamGenerateContent?key=${config.apiKey}`;
    const contents = buildGeminiContents(messages, config.model);
    const body = { contents };
    if (config.system && config.system.length > 0) {
      body["systemInstruction"] = { parts: [{ text: config.system }] };
    }
    const generationConfig = { maxOutputTokens: config.maxTokens };
    if (config.settings) {
      const settings = config.settings.toRequestParameters();
      for (const key of ["temperature", "topP", "topK", "stopSequences", "thinkingConfig"]) {
        if (settings[key] !== void 0) {
          generationConfig[key] = settings[key];
        }
      }
    }
    if (config.geminiCapabilities?.imageGenerationEnabled) {
      generationConfig["responseModalities"] = ["TEXT", "IMAGE"];
      if (config.geminiCapabilities.imageAspectRatio) {
        generationConfig["imageConfig"] = {
          aspectRatio: config.geminiCapabilities.imageAspectRatio
        };
      }
    }
    body["generationConfig"] = generationConfig;
    if (config.tools && config.tools.length > 0) {
      body["tools"] = ToolFormatters.geminiTools(config.tools);
      const toolConfig = ToolFormatters.geminiToolConfig(config.toolChoice);
      if (toolConfig !== null) {
        body["toolConfig"] = toolConfig;
      }
    }
    const response = await this.transport({
      url,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal
    });
    if (response.status !== 200) {
      await this.throwProviderError(response, "Gemini API error");
    }
    let accumulatedContent = "";
    let accumulatedReasoning = "";
    let lastTokens;
    const generatedImages = [];
    const completedToolCalls = [];
    let lastFinishReason;
    const accumulator = new StreamedJSONObjectAccumulator();
    for await (const chunk of this.byteChunks(response)) {
      for (const jsonBuffer of accumulator.consumeChunk(chunk)) {
        const objectJson = extractJsonObject(jsonBuffer);
        if (objectJson === null) continue;
        for (const event of SSEParser.parseGeminiChunk(objectJson)) {
          switch (event.type) {
            case "textDelta":
              accumulatedContent += event.text;
              yield StreamingEvents.textDelta(event.text);
              break;
            case "reasoningDelta":
              accumulatedReasoning += event.text;
              yield StreamingEvents.reasoningDelta(event.text);
              break;
            case "imageDelta":
              generatedImages.push({ data: event.data, mimeType: event.mimeType });
              break;
            case "functionCall": {
              const toolCall = {
                id: generateUUID(),
                name: event.name,
                arguments: JSON.stringify(event.args),
                thoughtSignature: event.thoughtSignature ?? void 0
              };
              completedToolCalls.push(toolCall);
              yield StreamingEvents.toolCallStart(toolCall);
              yield StreamingEvents.toolCallComplete(toolCall);
              break;
            }
            case "usageMetadata":
              lastTokens = {
                input: event.promptTokens,
                output: event.candidatesTokens,
                total: event.totalTokens,
                ...event.cachedTokens !== void 0 && { cachedInput: event.cachedTokens }
              };
              break;
            case "finishReason":
              lastFinishReason = event.reason;
              if ((event.reason === "STOP" || event.reason === "FUNCTION_CALL") && completedToolCalls.length > 0) {
                yield StreamingEvents.toolResultRequired(completedToolCalls);
              }
              yield StreamingEvents.completion({
                fullContent: accumulatedContent,
                reasoning: accumulatedReasoning.length > 0 ? accumulatedReasoning : void 0,
                tokens: lastTokens,
                modelName: config.model,
                providerName,
                generatedImages: generatedImages.length > 0 ? generatedImages : void 0,
                toolCalls: completedToolCalls.length > 0 ? completedToolCalls : void 0,
                stopReason: event.reason
              });
              return;
          }
        }
      }
    }
    if (accumulatedContent.length > 0 || generatedImages.length > 0 || completedToolCalls.length > 0 || accumulatedReasoning.length > 0) {
      if (completedToolCalls.length > 0) {
        yield StreamingEvents.toolResultRequired(completedToolCalls);
      }
      yield StreamingEvents.completion({
        fullContent: accumulatedContent,
        reasoning: accumulatedReasoning.length > 0 ? accumulatedReasoning : void 0,
        tokens: lastTokens,
        modelName: config.model,
        providerName,
        generatedImages: generatedImages.length > 0 ? generatedImages : void 0,
        toolCalls: completedToolCalls.length > 0 ? completedToolCalls : void 0,
        stopReason: lastFinishReason
      });
    }
  }
  // MARK: - DeepSeek Streaming
  async *streamDeepSeek(config, messages, providerName, signal) {
    const apiMessages = [];
    if (config.system && config.system.length > 0) {
      apiMessages.push({ role: "system", content: config.system });
    }
    for (const msg of messages) {
      if (msg.role === "system") continue;
      apiMessages.push({ role: msg.role, content: msg.content });
    }
    const body = {
      model: config.model,
      messages: apiMessages,
      stream: true
    };
    if (config.settings) {
      Object.assign(body, config.settings.toRequestParameters());
    }
    const response = await this.transport({
      url: "https://api.deepseek.com/chat/completions",
      method: "POST",
      headers: {
        Authorization: `Bearer ${config.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body),
      signal
    });
    if (response.status !== 200) {
      await this.throwProviderError(response, "DeepSeek API error");
    }
    let accumulatedContent = "";
    let accumulatedReasoning = "";
    for await (const line of this.lines(response)) {
      const data = SSEParser.parseDataLine(line);
      if (data === null) continue;
      const event = SSEParser.parseReasoningModelEvent(data);
      if (!event) continue;
      switch (event.type) {
        case "reasoningDelta":
          accumulatedReasoning += event.text;
          yield StreamingEvents.reasoningDelta(event.text);
          break;
        case "contentDelta":
          accumulatedContent += event.text;
          yield StreamingEvents.textDelta(event.text);
          break;
        case "done":
          yield StreamingEvents.completion({
            fullContent: accumulatedContent,
            reasoning: accumulatedReasoning.length > 0 ? accumulatedReasoning : void 0,
            tokens: void 0,
            modelName: config.model,
            providerName
          });
          return;
      }
    }
  }
};
function responsesTransportError(error, signal) {
  if (error instanceof StreamingError) return error;
  const name = error instanceof Error || error instanceof DOMException ? error.name : void 0;
  if (name === "TimeoutError") return StreamingError.timeout();
  if (name === "AbortError" || error instanceof APIError && error.kind === "cancelled" || signal.aborted) {
    return StreamingError.cancelled();
  }
  return error;
}

// src/client/extensions/openai/openai-agentic-responses.ts
var OpenAIAgenticResponsesClient = class {
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  transport;
  create(options) {
    return this.request("responses", "POST", options.apiKey, JSON.stringify(serializeResponsesRequest(options.request)), options.signal);
  }
  retrieve(options) {
    return this.request(`responses/${encodeURIComponent(options.id)}`, "GET", options.apiKey, void 0, options.signal);
  }
  cancel(options) {
    return this.request(`responses/${encodeURIComponent(options.id)}/cancel`, "POST", options.apiKey, void 0, options.signal);
  }
  async waitForCompletion(options) {
    const interval = options.pollIntervalMs ?? 1e3;
    for (; ; ) {
      const response = await this.retrieve(options);
      if (response.status !== "queued" && response.status !== "in_progress") return response;
      await new Promise((resolve, reject) => {
        const id = setTimeout(resolve, interval);
        options.signal?.addEventListener("abort", () => {
          clearTimeout(id);
          reject(APIError.cancelled());
        }, { once: true });
      });
    }
  }
  async *stream(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/responses",
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${options.apiKey}` },
      body: JSON.stringify({ ...serializeResponsesRequest(options.request), stream: true }),
      signal: options.signal
    });
    if (!response.ok) throw ErrorResponseParser.makeError(response.status, await response.text());
    if (!response.body) throw APIError.parseError("OpenAI Responses stream has no body");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let pending = "";
    for (; ; ) {
      const next = await reader.read();
      if (next.done) break;
      pending += decoder.decode(next.value, { stream: true });
      const lines = pending.split("\n");
      pending = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const event = decodeResponsesStreamEvent(line.slice(6));
        if (event) yield event;
      }
    }
  }
  async request(path, method, apiKey, body, signal) {
    const response = await this.transport({
      url: `https://api.openai.com/v1/${path}`,
      method,
      headers: { ...body ? { "Content-Type": "application/json" } : {}, Authorization: `Bearer ${apiKey}` },
      body,
      signal
    });
    const text = await response.text();
    if (!response.ok) throw ErrorResponseParser.makeError(response.status, text);
    try {
      const record = asRecord(JSON.parse(text));
      if (!record) throw new Error("not an object");
      return decodeResponsesResponse(record);
    } catch {
      throw APIError.parseError("Invalid OpenAI responses payload");
    }
  }
};
function decodeResponsesStreamEvent(data) {
  if (data === "[DONE]") return { type: "done" };
  try {
    const record = asRecord(JSON.parse(data));
    if (!record || !asString(record.type)) return void 0;
    const response = asRecord(record.response);
    return {
      type: asString(record.type),
      response: response ? decodeResponsesResponse(response) : void 0,
      outputIndex: typeof record.output_index === "number" ? record.output_index : void 0,
      itemId: asString(record.item_id),
      delta: asString(record.delta)
    };
  } catch {
    return void 0;
  }
}
async function verifyOpenAIWebhookSignature(payload, signature, secret) {
  const secretBytes = new TextEncoder().encode(secret);
  const payloadBytes = payload.buffer.slice(
    payload.byteOffset,
    payload.byteOffset + payload.byteLength
  );
  const key = await crypto.subtle.importKey(
    "raw",
    secretBytes.buffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const expected = new Uint8Array(await crypto.subtle.sign("HMAC", key, payloadBytes));
  const supplied = Uint8Array.from(atob(signature), (char) => char.charCodeAt(0));
  if (expected.length !== supplied.length) return false;
  let difference = 0;
  for (let index = 0; index < expected.length; index += 1) difference |= expected[index] ^ supplied[index];
  return difference === 0;
}

// src/client/extensions/openai/openai-web-search.ts
function serializeWebSearchOptions(options) {
  const body = {};
  if (options.userLocation !== void 0) {
    body["user_location"] = serializeUserLocation(options.userLocation);
  }
  return body;
}
function serializeWebSearchRequest(request) {
  const body = {
    model: request.model,
    messages: request.messages.map((message) => ({
      role: message.role,
      content: message.content
    }))
  };
  if (request.webSearchOptions !== void 0) {
    body["web_search_options"] = serializeWebSearchOptions(request.webSearchOptions);
  }
  if (request.temperature !== void 0) body["temperature"] = request.temperature;
  return body;
}
function decodeWebSearchAnnotation(record) {
  const citation = asRecord(record["url_citation"]);
  return {
    type: asString(record["type"]),
    urlCitation: citation ? decodeResponsesCitation(citation) : void 0
  };
}
function decodeWebSearchMessage(record) {
  const annotations = asArray(record["annotations"]);
  return {
    role: asString(record["role"]),
    content: asString(record["content"]),
    annotations: annotations ? annotations.flatMap((entry) => {
      const rec = asRecord(entry);
      return rec ? [decodeWebSearchAnnotation(rec)] : [];
    }) : void 0
  };
}
function decodeWebSearchResponse(record) {
  const choices = asArray(record["choices"]) ?? [];
  return {
    choices: choices.flatMap((entry) => {
      const rec = asRecord(entry);
      if (!rec) return [];
      const message = asRecord(rec["message"]) ?? {};
      return [
        {
          index: asNumber(rec["index"]),
          message: decodeWebSearchMessage(message),
          finishReason: asString(rec["finish_reason"])
        }
      ];
    })
  };
}
var OpenAIWebSearchClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Perform a web search request. */
  async search(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/chat/completions",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeWebSearchRequest(options.request)),
      signal: options.signal
    });
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError("Invalid OpenAI web search response");
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError("Invalid OpenAI web search response");
    return decodeWebSearchResponse(record);
  }
};

// src/client/extensions/openai/openai-deep-research.ts
function serializeDeepResearchRequest(request) {
  const body = {
    model: request.model,
    input: request.input,
    tools: request.tools.map(serializeResponsesTool)
  };
  if (request.background !== void 0) body["background"] = request.background;
  if (request.maxToolCalls !== void 0) body["max_tool_calls"] = request.maxToolCalls;
  if (request.reasoning !== void 0) {
    body["reasoning"] = serializeResponsesReasoning(request.reasoning);
  }
  return body;
}
var OpenAIDeepResearchClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Execute a deep research request via the Responses API. */
  async research(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/responses",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeDeepResearchRequest(options.request)),
      signal: options.signal
    });
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError("Invalid OpenAI responses payload");
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError("Invalid OpenAI responses payload");
    return decodeResponsesResponse(record);
  }
};

// src/client/extensions/openai/openai-computer-use.ts
function serializeComputerUseRequest(request) {
  const body = {
    model: request.model,
    tools: request.tools.map(serializeResponsesTool),
    input: request.input.map(serializeResponsesInputItem)
  };
  if (request.reasoning !== void 0) {
    body["reasoning"] = serializeResponsesReasoning(request.reasoning);
  }
  if (request.truncation !== void 0) body["truncation"] = request.truncation;
  if (request.previousResponseId !== void 0) {
    body["previous_response_id"] = request.previousResponseId;
  }
  return body;
}
var OpenAIComputerUseClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Execute a computer use step via the Responses API. */
  async step(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/responses",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeComputerUseRequest(options.request)),
      signal: options.signal
    });
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError("Invalid OpenAI responses payload");
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError("Invalid OpenAI responses payload");
    return decodeResponsesResponse(record);
  }
};

// src/client/extensions/openai/openai-image-generation.ts
function serializeGenerationRequest(request) {
  const body = { model: request.model, prompt: request.prompt };
  if (request.n !== void 0) body["n"] = request.n;
  if (request.size !== void 0) body["size"] = request.size;
  if (request.quality !== void 0) body["quality"] = request.quality;
  if (request.background !== void 0) body["background"] = request.background;
  if (request.responseFormat !== void 0) body["response_format"] = request.responseFormat;
  if (request.outputFormat !== void 0) body["output_format"] = request.outputFormat;
  if (request.outputCompression !== void 0) body["output_compression"] = request.outputCompression;
  if (request.moderation !== void 0) body["moderation"] = request.moderation;
  if (request.user !== void 0) body["user"] = request.user;
  return body;
}
function decodeImageResponse(record) {
  const data = asArray(record["data"]) ?? [];
  return {
    created: asNumber(record["created"]),
    data: data.flatMap((entry) => {
      const rec = asRecord(entry);
      if (!rec) return [];
      return [
        {
          url: asString(rec["url"]),
          b64Json: asString(rec["b64_json"]),
          revisedPrompt: asString(rec["revised_prompt"])
        }
      ];
    })
  };
}
function fileBlob(file) {
  return new Blob([file.data], { type: file.mimeType });
}
var OpenAIImageGenerationClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Generate images using the OpenAI Images API. */
  async generate(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/images/generations",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeGenerationRequest(options.request)),
      signal: options.signal
    });
    return this.decode(response);
  }
  /** Edit images using the OpenAI Images API (multipart form). */
  async edit(options) {
    const { request } = options;
    const form = new FormData();
    form.append("model", request.model);
    form.append("prompt", request.prompt);
    if (request.n !== void 0) form.append("n", String(request.n));
    if (request.size !== void 0) form.append("size", request.size);
    if (request.quality !== void 0) form.append("quality", request.quality);
    if (request.background !== void 0) form.append("background", request.background);
    if (request.responseFormat !== void 0) {
      form.append("response_format", request.responseFormat);
    }
    if (request.outputFormat !== void 0) form.append("output_format", request.outputFormat);
    if (request.outputCompression !== void 0) {
      form.append("output_compression", String(request.outputCompression));
    }
    if (request.inputFidelity !== void 0) {
      form.append("input_fidelity", request.inputFidelity);
    }
    if (request.user !== void 0) form.append("user", request.user);
    for (const image of request.images) {
      form.append("image[]", fileBlob(image), image.fileName);
    }
    if (request.mask !== void 0) {
      form.append("mask", fileBlob(request.mask), request.mask.fileName);
    }
    const response = await this.transport({
      url: "https://api.openai.com/v1/images/edits",
      method: "POST",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      body: form,
      signal: options.signal
    });
    return this.decode(response);
  }
  /** Create image variations (DALL-E 2 only). */
  async variation(options) {
    const { request } = options;
    const form = new FormData();
    form.append("model", request.model);
    if (request.n !== void 0) form.append("n", String(request.n));
    if (request.size !== void 0) form.append("size", request.size);
    if (request.responseFormat !== void 0) {
      form.append("response_format", request.responseFormat);
    }
    if (request.user !== void 0) form.append("user", request.user);
    form.append("image", fileBlob(request.image), request.image.fileName);
    const response = await this.transport({
      url: "https://api.openai.com/v1/images/variations",
      method: "POST",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      body: form,
      signal: options.signal
    });
    return this.decode(response);
  }
  async decode(response) {
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError("Invalid OpenAI image response");
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError("Invalid OpenAI image response");
    return decodeImageResponse(record);
  }
};

// src/client/extensions/openai/openai-video-generation.ts
function decodeVideoObject(record) {
  const error = asRecord(record["error"]);
  return {
    id: asString(record["id"]),
    object: asString(record["object"]),
    createdAt: asNumber(record["created_at"]),
    status: asString(record["status"]),
    model: asString(record["model"]),
    progress: asNumber(record["progress"]),
    seconds: asString(record["seconds"]),
    size: asString(record["size"]),
    error: error ? { message: asString(error["message"]) } : void 0
  };
}
var OpenAIVideoGenerationClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Start a Sora video generation job. */
  async create(options) {
    const { request } = options;
    const form = new FormData();
    form.append("model", request.model);
    form.append("prompt", request.prompt);
    if (request.size !== void 0) form.append("size", request.size);
    if (request.seconds !== void 0) form.append("seconds", String(request.seconds));
    if (request.inputReference !== void 0) {
      const reference = request.inputReference;
      form.append(
        "input_reference",
        new Blob([reference.data], { type: reference.mimeType }),
        reference.fileName
      );
    }
    const response = await this.transport({
      url: "https://api.openai.com/v1/videos",
      method: "POST",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      body: form,
      signal: options.signal
    });
    return decodeVideoObject(await this.decodeJSON(response, "Invalid OpenAI video response"));
  }
  /** Retrieve a video job status. */
  async retrieve(options) {
    const response = await this.transport({
      url: `https://api.openai.com/v1/videos/${options.id}`,
      method: "GET",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      signal: options.signal
    });
    return decodeVideoObject(await this.decodeJSON(response, "Invalid OpenAI video response"));
  }
  /** Download video content, thumbnail, or spritesheet. Returns raw bytes. */
  async download(options) {
    let url = `https://api.openai.com/v1/videos/${options.id}/content`;
    if (options.variant !== void 0) {
      url += `?${new URLSearchParams({ variant: options.variant }).toString()}`;
    }
    const response = await this.transport({
      url,
      method: "GET",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      signal: options.signal
    });
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, await response.text());
    }
    return new Uint8Array(await response.arrayBuffer());
  }
  /** List video generation jobs. */
  async list(options) {
    const query = new URLSearchParams();
    if (options.limit !== void 0) query.append("limit", String(options.limit));
    if (options.after !== void 0) query.append("after", options.after);
    if (options.order !== void 0) query.append("order", options.order);
    const suffix = query.toString();
    const url = suffix.length > 0 ? `https://api.openai.com/v1/videos?${suffix}` : "https://api.openai.com/v1/videos";
    const response = await this.transport({
      url,
      method: "GET",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      signal: options.signal
    });
    const record = await this.decodeJSON(response, "Invalid OpenAI video list response");
    const data = record["data"];
    return {
      data: Array.isArray(data) ? data.flatMap((entry) => {
        const rec = asRecord(entry);
        return rec ? [decodeVideoObject(rec)] : [];
      }) : void 0,
      firstId: asString(record["first_id"]),
      lastId: asString(record["last_id"]),
      hasMore: asBoolean(record["has_more"])
    };
  }
  /** Delete a video job. */
  async delete(options) {
    const response = await this.transport({
      url: `https://api.openai.com/v1/videos/${options.id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      signal: options.signal
    });
    const record = await this.decodeJSON(response, "Invalid OpenAI video delete response");
    return {
      id: asString(record["id"]),
      deleted: asBoolean(record["deleted"])
    };
  }
  async decodeJSON(response, parseMessage) {
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError(parseMessage);
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError(parseMessage);
    return record;
  }
};

// src/client/extensions/openai/openai-embeddings.ts
function serializeEmbeddingsRequest(request) {
  const input = typeof request.input === "string" ? request.input : [...request.input];
  const body = { model: request.model, input };
  if (request.encodingFormat !== void 0) body["encoding_format"] = request.encodingFormat;
  if (request.dimensions !== void 0) body["dimensions"] = request.dimensions;
  if (request.user !== void 0) body["user"] = request.user;
  return body;
}
function decodeEmbeddingsResponse(record) {
  const data = asArray(record["data"]) ?? [];
  const usage = asRecord(record["usage"]);
  return {
    data: data.flatMap((entry) => {
      const rec = asRecord(entry);
      if (!rec) return [];
      const embedding = asArray(rec["embedding"]) ?? [];
      const values = [];
      for (const value of embedding) if (typeof value === "number") values.push(value);
      return [
        {
          index: asNumber(rec["index"]),
          embedding: values,
          object: asString(rec["object"])
        }
      ];
    }),
    model: asString(record["model"]),
    usage: usage ? {
      promptTokens: asNumber(usage["prompt_tokens"]),
      totalTokens: asNumber(usage["total_tokens"])
    } : void 0
  };
}
var OpenAIEmbeddingsClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Create vector embeddings. */
  async create(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/embeddings",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeEmbeddingsRequest(options.request)),
      signal: options.signal
    });
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw APIError.parseError("Invalid OpenAI embeddings response");
    }
    const record = asRecord(parsed);
    if (!record) throw APIError.parseError("Invalid OpenAI embeddings response");
    return decodeEmbeddingsResponse(record);
  }
};

// src/client/extensions/openai/openai-audio-transcription.ts
function decodeTranscriptionResponse(record) {
  const text = asString(record["text"]);
  if (text === void 0) return null;
  const segments = asArray(record["segments"]);
  return {
    text,
    segments: segments ? segments.flatMap((entry) => {
      const rec = asRecord(entry);
      if (!rec) return [];
      return [
        {
          id: asNumber(rec["id"]),
          start: asNumber(rec["start"]),
          end: asNumber(rec["end"]),
          text: asString(rec["text"])
        }
      ];
    }) : void 0,
    language: asString(record["language"])
  };
}
var OpenAIAudioTranscriptionClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Transcribe audio. Falls back to the raw text body for non-JSON formats. */
  async transcribe(options) {
    const { request } = options;
    const form = new FormData();
    form.append("model", request.model);
    if (request.language !== void 0) form.append("language", request.language);
    if (request.prompt !== void 0) form.append("prompt", request.prompt);
    if (request.responseFormat !== void 0) {
      form.append("response_format", request.responseFormat);
    }
    if (request.temperature !== void 0) {
      form.append("temperature", String(request.temperature));
    }
    if (request.timestampGranularities !== void 0) {
      for (const granularity of request.timestampGranularities) {
        form.append("timestamp_granularities[]", granularity);
      }
    }
    const fileBlob2 = new Blob([request.file.data], { type: request.file.mimeType });
    form.append("file", fileBlob2, request.file.fileName);
    const response = await this.transport({
      url: "https://api.openai.com/v1/audio/transcriptions",
      method: "POST",
      headers: { Authorization: `Bearer ${options.apiKey}` },
      body: form,
      signal: options.signal
    });
    const text = await response.text();
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, text);
    }
    try {
      const record = asRecord(JSON.parse(text));
      if (record) {
        const decoded = decodeTranscriptionResponse(record);
        if (decoded) return decoded;
      }
    } catch {
    }
    return { text, segments: void 0, language: void 0 };
  }
};

// src/client/extensions/openai/openai-tts.ts
var OpenAITTSVoice = {
  alloy: "alloy",
  ash: "ash",
  ballad: "ballad",
  coral: "coral",
  echo: "echo",
  fable: "fable",
  nova: "nova",
  onyx: "onyx",
  sage: "sage",
  shimmer: "shimmer"
};
function serializeVoice(voice) {
  return typeof voice === "string" ? voice : { id: voice.id };
}
function serializeTTSRequest(request) {
  const body = {
    model: request.model,
    input: request.input,
    voice: serializeVoice(request.voice)
  };
  if (request.instructions !== void 0) body["instructions"] = request.instructions;
  if (request.responseFormat !== void 0) body["response_format"] = request.responseFormat;
  if (request.speed !== void 0) body["speed"] = request.speed;
  if (request.language !== void 0) body["language"] = request.language;
  return body;
}
var OpenAITTSClient = class {
  transport;
  constructor(transport = defaultTransport) {
    this.transport = transport;
  }
  /** Generate speech audio. Returns the raw audio bytes. */
  async speech(options) {
    const response = await this.transport({
      url: "https://api.openai.com/v1/audio/speech",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${options.apiKey}`
      },
      body: JSON.stringify(serializeTTSRequest(options.request)),
      signal: options.signal
    });
    if (!(response.status >= 200 && response.status < 300)) {
      throw ErrorResponseParser.makeError(response.status, await response.text());
    }
    return new Uint8Array(await response.arrayBuffer());
  }
};

// src/client/extensions/gemini/gemini-thinking.ts
async function geminiThinking(options) {
  const { request, apiKey } = options;
  const transport = options.transport ?? defaultTransport;
  const modelId = request.model.startsWith("models/") ? request.model : `models/${request.model}`;
  const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:generateContent?key=${apiKey}`;
  const payload = {
    contents: request.contents.map((content) => ({
      role: content.role,
      parts: content.parts.map((part) => {
        const partPayload = {};
        if (part.text !== void 0 && part.text !== null) {
          partPayload["text"] = part.text;
        }
        return partPayload;
      })
    }))
  };
  const generationConfig = request.generationConfig;
  if (generationConfig) {
    const generationPayload = {};
    if (generationConfig.maxOutputTokens !== void 0) {
      generationPayload["maxOutputTokens"] = generationConfig.maxOutputTokens;
    }
    if (generationConfig.temperature !== void 0) {
      generationPayload["temperature"] = generationConfig.temperature;
    }
    if (generationConfig.topP !== void 0) {
      generationPayload["topP"] = generationConfig.topP;
    }
    if (generationConfig.topK !== void 0) {
      generationPayload["topK"] = generationConfig.topK;
    }
    const thinkingConfig = generationConfig.thinkingConfig;
    if (thinkingConfig) {
      const thinkingPayload = {};
      if (thinkingConfig.thinkingBudget !== void 0) {
        thinkingPayload["thinkingBudget"] = thinkingConfig.thinkingBudget;
      }
      if (thinkingConfig.thinkingLevel !== void 0) {
        thinkingPayload["thinkingLevel"] = thinkingConfig.thinkingLevel;
      }
      if (thinkingConfig.includeThoughts !== void 0) {
        thinkingPayload["includeThoughts"] = thinkingConfig.includeThoughts;
      }
      if (Object.keys(thinkingPayload).length > 0) {
        generationPayload["thinkingConfig"] = thinkingPayload;
      }
    }
    if (Object.keys(generationPayload).length > 0) {
      payload["generationConfig"] = generationPayload;
    }
  }
  const response = await transport({
    url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  try {
    return JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid Gemini thinking response");
  }
}
function geminiThinkingOutput(response) {
  const parts = response.candidates?.[0]?.content?.parts;
  if (!parts) {
    throw APIError.parseError("Empty response");
  }
  let answer = "";
  let thoughts = "";
  for (const part of parts) {
    if (part.thought === true) {
      thoughts += part.text ?? "";
    } else {
      answer += part.text ?? "";
    }
  }
  const trimmedAnswer = answer.trim();
  if (trimmedAnswer.length === 0) {
    throw APIError.parseError("Empty response");
  }
  const trimmedThoughts = thoughts.trim();
  return {
    text: trimmedAnswer,
    thoughts: trimmedThoughts.length === 0 ? void 0 : trimmedThoughts
  };
}

// src/client/extensions/gemini/gemini-audio-understanding.ts
async function geminiAudioUnderstanding(options) {
  const { request, apiKey } = options;
  const transport = options.transport ?? defaultTransport;
  const modelId = request.model.startsWith("models/") ? request.model : `models/${request.model}`;
  const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:generateContent?key=${apiKey}`;
  const payload = {
    contents: request.contents.map((content) => ({
      role: content.role,
      parts: content.parts.map((part) => {
        const partPayload = {};
        if (part.text !== void 0 && part.text !== null) {
          partPayload["text"] = part.text;
        }
        if (part.inlineData) {
          partPayload["inline_data"] = {
            mime_type: part.inlineData.mimeType,
            data: part.inlineData.data
          };
        }
        if (part.fileData) {
          partPayload["file_data"] = {
            file_uri: part.fileData.fileUri,
            mime_type: part.fileData.mimeType
          };
        }
        return partPayload;
      })
    }))
  };
  const generationConfig = request.generationConfig;
  if (generationConfig) {
    payload["generationConfig"] = {
      maxOutputTokens: generationConfig.maxOutputTokens ?? null,
      temperature: generationConfig.temperature ?? null,
      topP: generationConfig.topP ?? null,
      topK: generationConfig.topK ?? null
    };
  }
  const response = await transport({
    url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  try {
    return JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid Gemini audio understanding response");
  }
}
async function geminiAudioUnderstandingText(options) {
  const model = options.model ?? "gemini-2.5-flash";
  const content = {
    role: "user",
    parts: [{ text: options.input.prompt }, options.input.audio]
  };
  const request = { model, contents: [content] };
  const response = await geminiAudioUnderstanding({
    request,
    apiKey: options.apiKey,
    ...options.transport !== void 0 && { transport: options.transport },
    signal: options.signal
  });
  const text = response.candidates?.[0]?.content?.parts?.map((part) => part.text).filter((value) => value !== void 0 && value !== null).join("");
  if (!text || text.length === 0) {
    throw APIError.parseError("Empty response");
  }
  return text;
}

// src/client/extensions/gemini/gemini-grounding-with-google-search.ts
var GEMINI_GROUNDING_GOOGLE_SEARCH_TOOL = {
  googleSearch: {}
};
async function geminiGroundingWithGoogleSearch(options) {
  const { request, apiKey } = options;
  const transport = options.transport ?? defaultTransport;
  const modelId = request.model.startsWith("models/") ? request.model : `models/${request.model}`;
  const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:generateContent?key=${apiKey}`;
  const payload = {
    contents: request.contents.map((content) => ({
      role: content.role,
      parts: content.parts.map((part) => ({ text: part.text ?? "" }))
    }))
  };
  if (request.tools) {
    payload["tools"] = request.tools.map((tool) => ({
      google_search: tool.googleSearch ?? {}
    }));
  }
  const generationConfig = request.generationConfig;
  if (generationConfig) {
    payload["generationConfig"] = {
      maxOutputTokens: generationConfig.maxOutputTokens ?? null,
      temperature: generationConfig.temperature ?? null,
      topP: generationConfig.topP ?? null,
      topK: generationConfig.topK ?? null
    };
  }
  const response = await transport({
    url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  try {
    return JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid Gemini grounding response");
  }
}
async function geminiGroundingText(options) {
  const model = options.model ?? "gemini-3-pro-preview";
  const content = {
    role: "user",
    parts: [{ text: options.prompt }]
  };
  const request = {
    model,
    contents: [content],
    tools: [GEMINI_GROUNDING_GOOGLE_SEARCH_TOOL]
  };
  const response = await geminiGroundingWithGoogleSearch({
    request,
    apiKey: options.apiKey,
    ...options.transport !== void 0 && { transport: options.transport },
    signal: options.signal
  });
  const output = response.candidates?.[0]?.content?.parts?.map((part) => part.text).filter((value) => value !== void 0 && value !== null).join("");
  if (!output || output.length === 0) {
    throw APIError.parseError("Empty response");
  }
  return output;
}
function geminiGroundingInlineCitations(response) {
  const candidate = response.candidates?.[0];
  const text = candidate?.content?.parts?.map((part) => part.text).filter((value) => value !== void 0 && value !== null).join("");
  if (!candidate || text === void 0) {
    return null;
  }
  const supports = candidate.groundingMetadata?.groundingSupports;
  const chunks = candidate.groundingMetadata?.groundingChunks;
  if (!supports || !chunks) {
    return text;
  }
  const sortedSupports = [...supports].sort(
    (a, b) => (b.segment?.endIndex ?? 0) - (a.segment?.endIndex ?? 0)
  );
  let output = text;
  for (const support of sortedSupports) {
    const endIndex = support.segment?.endIndex;
    const indices = support.groundingChunkIndices;
    if (endIndex === void 0 || !indices || indices.length === 0) {
      continue;
    }
    const citations = indices.map((index) => {
      const uri = chunks[index]?.web?.uri;
      if (index >= chunks.length || uri === void 0 || uri === null) {
        return null;
      }
      return `[${index + 1}](${uri})`;
    }).filter((value) => value !== null);
    if (citations.length === 0) {
      continue;
    }
    const insertionIndex = Math.min(endIndex, output.length);
    output = output.slice(0, insertionIndex) + " " + citations.join(", ") + output.slice(insertionIndex);
  }
  return output;
}

// src/client/extensions/gemini/gemini-image-generation.ts
async function geminiImageGeneration(options) {
  const { request, apiKey } = options;
  const transport = options.transport ?? defaultTransport;
  const modelId = request.model.startsWith("models/") ? request.model : `models/${request.model}`;
  const url = `https://generativelanguage.googleapis.com/v1beta/${modelId}:generateContent?key=${apiKey}`;
  const payload = {
    contents: request.contents.map((content) => ({
      role: content.role,
      parts: content.parts.map((part) => {
        const partPayload = {};
        if (part.text !== void 0 && part.text !== null) {
          partPayload["text"] = part.text;
        }
        if (part.inlineData) {
          partPayload["inline_data"] = {
            mime_type: part.inlineData.mimeType,
            data: part.inlineData.data
          };
        }
        if (part.fileData) {
          partPayload["file_data"] = {
            file_uri: part.fileData.fileUri,
            mime_type: part.fileData.mimeType
          };
        }
        return partPayload;
      })
    }))
  };
  const generationConfig = request.generationConfig;
  if (generationConfig) {
    const configPayload = {};
    if (generationConfig.responseModalities !== void 0) {
      configPayload["responseModalities"] = generationConfig.responseModalities;
    }
    const aspectRatio = generationConfig.imageConfig?.aspectRatio;
    if (aspectRatio !== void 0 && aspectRatio !== null) {
      configPayload["imageConfig"] = { aspectRatio };
    }
    if (Object.keys(configPayload).length > 0) {
      payload["generationConfig"] = configPayload;
    }
  }
  const response = await transport({
    url,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  try {
    return JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid Gemini image generation response");
  }
}
async function geminiImageGenerationImages(options) {
  const model = options.model ?? "gemini-2.5-flash-image";
  const parts = [
    { text: options.input.prompt },
    ...options.input.images ?? []
  ];
  const content = { role: "user", parts };
  const config = {
    responseModalities: options.responseModalities,
    imageConfig: options.aspectRatio === void 0 ? void 0 : { aspectRatio: options.aspectRatio }
  };
  const request = {
    model,
    contents: [content],
    generationConfig: config
  };
  return geminiImageGeneration({
    request,
    apiKey: options.apiKey,
    ...options.transport !== void 0 && { transport: options.transport },
    signal: options.signal
  });
}

// src/client/extensions/gemini/gemini-live-api.ts
var GeminiLiveModel = {
  geminiFlashNativeAudioPreview: "gemini-2.5-flash-native-audio-preview-12-2025"
};
function encodeGeminiLiveClientMessage(message) {
  switch (message.kind) {
    case "setup":
      return { setup: encodeConfig(message.config) };
    case "realtimeInput":
      return { realtime_input: encodeRealtimeInput(message.input) };
    case "clientContent":
      return { client_content: encodeClientContent(message.content) };
    case "toolResponse":
      return { tool_response: encodeToolResponse(message.response) };
  }
}
function encodeConfig(config) {
  const out = {};
  if (config.responseModalities !== void 0) {
    out["response_modalities"] = config.responseModalities;
  }
  if (config.systemInstruction !== void 0) {
    out["system_instruction"] = config.systemInstruction;
  }
  if (config.tools !== void 0) {
    out["tools"] = config.tools.map(encodeTool);
  }
  if (config.speechConfig !== void 0) {
    out["speech_config"] = encodeSpeechConfig(config.speechConfig);
  }
  if (config.thinkingConfig !== void 0) {
    out["thinking_config"] = encodeThinkingConfig(config.thinkingConfig);
  }
  if (config.realtimeInputConfig !== void 0) {
    out["realtime_input_config"] = encodeRealtimeInputConfig(config.realtimeInputConfig);
  }
  if (config.outputAudioTranscription !== void 0) {
    out["output_audio_transcription"] = { ...config.outputAudioTranscription };
  }
  if (config.inputAudioTranscription !== void 0) {
    out["input_audio_transcription"] = { ...config.inputAudioTranscription };
  }
  if (config.mediaResolution !== void 0) {
    out["media_resolution"] = config.mediaResolution;
  }
  if (config.enableAffectiveDialog !== void 0) {
    out["enable_affective_dialog"] = config.enableAffectiveDialog;
  }
  if (config.proactivity !== void 0) {
    out["proactivity"] = encodeProactivity(config.proactivity);
  }
  if (config.contextWindowCompression !== void 0) {
    out["context_window_compression"] = encodeContextWindowCompression(
      config.contextWindowCompression
    );
  }
  if (config.sessionResumption !== void 0) {
    out["session_resumption"] = encodeSessionResumption(config.sessionResumption);
  }
  return out;
}
function encodeTool(tool) {
  const out = {};
  if (tool.googleSearch !== void 0) {
    out["google_search"] = { ...tool.googleSearch };
  }
  if (tool.functionDeclarations !== void 0) {
    out["function_declarations"] = tool.functionDeclarations.map(
      encodeFunctionDeclaration
    );
  }
  return out;
}
function encodeFunctionDeclaration(declaration) {
  const out = { name: declaration.name };
  if (declaration.description !== void 0) {
    out["description"] = declaration.description;
  }
  if (declaration.parameters !== void 0) {
    out["parameters"] = encodeFunctionParameters(declaration.parameters);
  }
  if (declaration.behavior !== void 0) {
    out["behavior"] = declaration.behavior;
  }
  return out;
}
function encodeFunctionParameters(parameters) {
  const out = { type: parameters.type };
  if (parameters.properties !== void 0) {
    const props = {};
    for (const [key, value] of Object.entries(parameters.properties)) {
      props[key] = encodeFunctionProperty(value);
    }
    out["properties"] = props;
  }
  if (parameters.required !== void 0) {
    out["required"] = parameters.required;
  }
  return out;
}
function encodeFunctionProperty(property) {
  const out = { type: property.type };
  if (property.description !== void 0) {
    out["description"] = property.description;
  }
  return out;
}
function encodeSpeechConfig(speech) {
  const out = {};
  if (speech.voiceConfig !== void 0) {
    const voice = {};
    if (speech.voiceConfig.prebuiltVoiceConfig !== void 0) {
      const prebuilt = {};
      if (speech.voiceConfig.prebuiltVoiceConfig.voiceName !== void 0) {
        prebuilt["voice_name"] = speech.voiceConfig.prebuiltVoiceConfig.voiceName;
      }
      voice["prebuilt_voice_config"] = prebuilt;
    }
    out["voice_config"] = voice;
  }
  return out;
}
function encodeThinkingConfig(thinking) {
  const out = {};
  if (thinking.thinkingBudget !== void 0) {
    out["thinking_budget"] = thinking.thinkingBudget;
  }
  if (thinking.includeThoughts !== void 0) {
    out["include_thoughts"] = thinking.includeThoughts;
  }
  return out;
}
function encodeRealtimeInputConfig(config) {
  const out = {};
  if (config.automaticActivityDetection !== void 0) {
    const detection = config.automaticActivityDetection;
    const inner = {};
    if (detection.disabled !== void 0) {
      inner["disabled"] = detection.disabled;
    }
    if (detection.startOfSpeechSensitivity !== void 0) {
      inner["start_of_speech_sensitivity"] = detection.startOfSpeechSensitivity;
    }
    if (detection.endOfSpeechSensitivity !== void 0) {
      inner["end_of_speech_sensitivity"] = detection.endOfSpeechSensitivity;
    }
    if (detection.prefixPaddingMs !== void 0) {
      inner["prefix_padding_ms"] = detection.prefixPaddingMs;
    }
    if (detection.silenceDurationMs !== void 0) {
      inner["silence_duration_ms"] = detection.silenceDurationMs;
    }
    out["automatic_activity_detection"] = inner;
  }
  return out;
}
function encodeProactivity(proactivity) {
  const out = {};
  if (proactivity.proactiveAudio !== void 0) {
    out["proactive_audio"] = proactivity.proactiveAudio;
  }
  return out;
}
function encodeContextWindowCompression(compression) {
  const out = {};
  if (compression.slidingWindow !== void 0) {
    out["sliding_window"] = { ...compression.slidingWindow };
  }
  if (compression.triggerTokens !== void 0) {
    out["trigger_tokens"] = compression.triggerTokens;
  }
  return out;
}
function encodeSessionResumption(resumption) {
  const out = {};
  if (resumption.handle !== void 0) {
    out["handle"] = resumption.handle;
  }
  return out;
}
function encodeRealtimeInput(input) {
  const out = {};
  if (input.audio !== void 0) {
    out["audio"] = { data: input.audio.data, mime_type: input.audio.mimeType };
  }
  if (input.activityStart !== void 0) {
    out["activity_start"] = { ...input.activityStart };
  }
  if (input.activityEnd !== void 0) {
    out["activity_end"] = { ...input.activityEnd };
  }
  if (input.audioStreamEnd !== void 0) {
    out["audio_stream_end"] = input.audioStreamEnd;
  }
  return out;
}
function encodeClientContent(content) {
  const out = { turns: encodeTurns(content.turns) };
  if (content.turnComplete !== void 0) {
    out["turn_complete"] = content.turnComplete;
  }
  return out;
}
function encodeTurns(turns) {
  if (turns.kind === "text") {
    return turns.text;
  }
  return turns.turns.map((turn) => ({
    role: turn.role,
    parts: turn.parts.map((part) => {
      const out = {};
      if (part.text !== void 0 && part.text !== null) {
        out["text"] = part.text;
      }
      return out;
    })
  }));
}
function encodeToolResponse(response) {
  return {
    function_responses: response.functionResponses.map((fr) => ({
      id: fr.id,
      name: fr.name,
      response: { ...fr.response }
    }))
  };
}
function decodeGeminiLiveServerEvent(value) {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const record = value;
    if ("serverContent" in record) {
      return {
        kind: "serverContent",
        serverContent: record["serverContent"]
      };
    }
    if ("toolCall" in record) {
      return { kind: "toolCall", toolCall: record["toolCall"] };
    }
    if ("sessionResumptionUpdate" in record) {
      return {
        kind: "sessionResumptionUpdate",
        sessionResumptionUpdate: record["sessionResumptionUpdate"]
      };
    }
    if ("goAway" in record) {
      return { kind: "goAway", goAway: record["goAway"] };
    }
    if ("usageMetadata" in record) {
      return {
        kind: "usageMetadata",
        usageMetadata: record["usageMetadata"]
      };
    }
  }
  throw APIError.parseError("Unknown Gemini Live API event");
}
function geminiLiveEventsFrom(event) {
  switch (event.kind) {
    case "serverContent":
      return mapServerContent(event.serverContent);
    case "toolCall":
      return [{ type: "toolCall", toolCall: event.toolCall }];
    case "usageMetadata":
      return [{ type: "usage", usage: event.usageMetadata }];
    case "sessionResumptionUpdate": {
      const handle = event.sessionResumptionUpdate.newHandle;
      if (handle !== void 0 && handle !== null) {
        return [{ type: "sessionResumption", handle }];
      }
      return [];
    }
    case "goAway":
      if (event.goAway.timeLeft !== void 0 && event.goAway.timeLeft !== null) {
        return [{ type: "generationComplete" }];
      }
      return [];
  }
}
function mapServerContent(content) {
  const events = [];
  if (content.modelTurn) {
    for (const part of content.modelTurn.parts ?? []) {
      if (part.text !== void 0 && part.text !== null) {
        events.push({ type: "text", text: part.text });
      }
      if (part.inlineData) {
        const data = base64ToBytes(part.inlineData.data);
        if (data) {
          events.push({ type: "audio", data, mimeType: part.inlineData.mimeType });
        }
      }
    }
  }
  if (content.interrupted === true) {
    events.push({ type: "interrupted" });
  }
  if (content.turnComplete === true) {
    events.push({ type: "turnComplete" });
  }
  if (content.generationComplete === true) {
    events.push({ type: "generationComplete" });
  }
  return events;
}
function base64ToBytes(base64) {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}
var GeminiLiveAPIClient = class {
  apiKey;
  model;
  webSocketFactory;
  ws;
  queue = [];
  waiters = [];
  // Chain frame processing so async (Blob) decoding preserves ordering.
  processing = Promise.resolve();
  constructor(options) {
    this.apiKey = options.apiKey;
    this.model = options.model ?? GeminiLiveModel.geminiFlashNativeAudioPreview;
    this.webSocketFactory = options.webSocketFactory ?? ((url) => new WebSocket(url));
  }
  /** Open the WebSocket and send the setup frame built from `config`. */
  async connect(config) {
    const modelId = this.model.startsWith("models/") ? this.model : `models/${this.model}`;
    const url = `wss://generativelanguage.googleapis.com/v1alpha/${modelId}:streamGenerateContent?key=${this.apiKey}`;
    const ws = this.webSocketFactory(url);
    this.ws = ws;
    ws.onmessage = (event) => {
      this.handleFrame(event.data);
    };
    ws.onclose = () => {
      this.enqueue({ kind: "close" });
    };
    ws.onerror = () => {
      this.enqueue({ kind: "error", error: APIError.networkError("WebSocket error") });
    };
    await this.waitForOpen(ws);
    await this.send({ kind: "setup", config });
  }
  /** Send a client message envelope over the socket. */
  send(message) {
    if (!this.ws) {
      throw APIError.networkError("Live API not connected");
    }
    const wire = encodeGeminiLiveClientMessage(message);
    this.ws.send(JSON.stringify(wire));
  }
  /** Send structured/plain-text client content (a conversational turn). */
  sendClientContent(content) {
    this.send({ kind: "clientContent", content });
  }
  /** Send a realtime input frame (e.g. streamed audio). */
  sendRealtimeInput(input) {
    this.send({ kind: "realtimeInput", input });
  }
  /** Send a tool/function response frame. */
  sendToolResponse(response) {
    this.send({ kind: "toolResponse", response });
  }
  /** Convenience: send a single text turn. */
  sendText(text, turnComplete = true) {
    this.sendClientContent({ turns: { kind: "text", text }, turnComplete });
  }
  /** Convenience: send a realtime audio blob. */
  sendAudio(data, mimeType) {
    this.sendRealtimeInput({ audio: { data, mimeType } });
  }
  /** Await the next parsed inbound server event. */
  async receive() {
    const item = await this.nextItem();
    if (item.kind === "close") {
      throw APIError.networkError("Live API connection closed");
    }
    if (item.kind === "error") {
      throw item.error;
    }
    return item.event;
  }
  /** Async generator of parsed inbound server events until the socket closes. */
  async *messages() {
    while (true) {
      const item = await this.nextItem();
      if (item.kind === "close") {
        return;
      }
      if (item.kind === "error") {
        throw item.error;
      }
      yield item.event;
    }
  }
  /** Close the WebSocket connection. */
  close() {
    if (!this.ws) {
      return;
    }
    this.ws.close(1e3);
    this.ws = void 0;
    this.enqueue({ kind: "close" });
  }
  // MARK: - Internals
  waitForOpen(ws) {
    if (ws.readyState === 1) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      ws.onopen = () => resolve();
      const previousOnError = ws.onerror;
      ws.onerror = (event) => {
        if (typeof previousOnError === "function") {
          previousOnError.call(ws, event);
        }
        reject(APIError.networkError("WebSocket connection failed"));
      };
    });
  }
  handleFrame(data) {
    this.processing = this.processing.then(async () => {
      let text;
      try {
        text = await frameToString(data);
      } catch (error) {
        this.enqueue({ kind: "error", error });
        return;
      }
      try {
        const parsed = JSON.parse(text);
        this.enqueue({ kind: "event", event: decodeGeminiLiveServerEvent(parsed) });
      } catch (error) {
        this.enqueue({ kind: "error", error });
      }
    });
  }
  enqueue(item) {
    const waiter = this.waiters.shift();
    if (waiter) {
      waiter(item);
    } else {
      this.queue.push(item);
    }
  }
  nextItem() {
    const queued = this.queue.shift();
    if (queued) {
      return Promise.resolve(queued);
    }
    return new Promise((resolve) => {
      this.waiters.push(resolve);
    });
  }
};
async function frameToString(data) {
  if (typeof data === "string") {
    return data;
  }
  if (data instanceof ArrayBuffer) {
    return new TextDecoder().decode(new Uint8Array(data));
  }
  if (ArrayBuffer.isView(data)) {
    const view = data;
    return new TextDecoder().decode(
      new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
    );
  }
  if (typeof data === "object" && data !== null && typeof data.arrayBuffer === "function") {
    const buffer = await data.arrayBuffer();
    return new TextDecoder().decode(new Uint8Array(buffer));
  }
  throw APIError.networkError("Unknown Live API message");
}

// src/client/extensions/grok/grok-tooling.ts
function encodeInput(input) {
  return { role: input.role, content: input.content };
}
function encodeGrokWebSearchOptions(options) {
  const out = {};
  if (options.allowedDomains !== void 0) out["allowed_domains"] = [...options.allowedDomains];
  if (options.excludedDomains !== void 0) out["excluded_domains"] = [...options.excludedDomains];
  if (options.enableImageUnderstanding !== void 0) {
    out["enable_image_understanding"] = options.enableImageUnderstanding;
  }
  return out;
}
function encodeGrokXSearchOptions(options) {
  const out = {};
  if (options.allowedXHandles !== void 0) out["allowed_x_handles"] = [...options.allowedXHandles];
  if (options.excludedXHandles !== void 0) {
    out["excluded_x_handles"] = [...options.excludedXHandles];
  }
  if (options.fromDate !== void 0) out["from_date"] = options.fromDate;
  if (options.toDate !== void 0) out["to_date"] = options.toDate;
  if (options.enableImageUnderstanding !== void 0) {
    out["enable_image_understanding"] = options.enableImageUnderstanding;
  }
  if (options.enableVideoUnderstanding !== void 0) {
    out["enable_video_understanding"] = options.enableVideoUnderstanding;
  }
  return out;
}
function encodeGrokToolDescriptor(tool) {
  const out = { type: tool.type };
  if (tool.webSearch !== void 0) out["web_search"] = encodeGrokWebSearchOptions(tool.webSearch);
  if (tool.xSearch !== void 0) out["x_search"] = encodeGrokXSearchOptions(tool.xSearch);
  return out;
}
function encodeGrokResponsesRequest(request) {
  const out = {
    model: request.model,
    input: request.input.map(encodeInput),
    tools: request.tools.map(encodeGrokToolDescriptor)
  };
  if (request.stream !== void 0) out["stream"] = request.stream;
  return out;
}
function asRecord3(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
}
function asString2(value) {
  return typeof value === "string" ? value : void 0;
}
function asNumber2(value) {
  return typeof value === "number" ? value : void 0;
}
function asStringArray2(value) {
  if (!Array.isArray(value)) return void 0;
  return value.filter((entry) => typeof entry === "string");
}
function decodeContent(value) {
  const record = asRecord3(value) ?? {};
  return { type: asString2(record["type"]), text: asString2(record["text"]) };
}
function decodeOutput(value) {
  const record = asRecord3(value) ?? {};
  const content = record["content"];
  return {
    type: asString2(record["type"]),
    content: Array.isArray(content) ? content.map(decodeContent) : void 0,
    text: asString2(record["text"])
  };
}
function decodeToolCall(value) {
  const record = asRecord3(value) ?? {};
  const fn = asRecord3(record["function"]);
  return {
    id: asString2(record["id"]),
    type: asString2(record["type"]),
    function: fn ? { name: asString2(fn["name"]), arguments: asString2(fn["arguments"]) } : void 0
  };
}
function decodeUsage(value) {
  const record = asRecord3(value);
  if (!record) return void 0;
  return {
    promptTokens: asNumber2(record["prompt_tokens"]),
    completionTokens: asNumber2(record["completion_tokens"]),
    totalTokens: asNumber2(record["total_tokens"])
  };
}
function decodeGrokResponsesResponse(value) {
  const record = asRecord3(value) ?? {};
  const output = record["output"];
  const toolCalls = record["tool_calls"];
  return {
    id: asString2(record["id"]),
    created: asNumber2(record["created"]),
    output: Array.isArray(output) ? output.map(decodeOutput) : void 0,
    outputText: asString2(record["output_text"]),
    citations: asStringArray2(record["citations"]),
    toolCalls: Array.isArray(toolCalls) ? toolCalls.map(decodeToolCall) : void 0,
    usage: decodeUsage(record["usage"])
  };
}
function grokResponsesCombinedText(response) {
  if (response.outputText !== void 0 && response.outputText.length > 0) {
    return response.outputText;
  }
  const contentText = (response.output ?? []).flatMap((item) => item.content ?? []).map((content) => content.text ?? "").join("");
  return contentText.length > 0 ? contentText : void 0;
}
async function grokResponses(options) {
  const transport = options.transport ?? defaultTransport;
  const body = encodeGrokResponsesRequest(options.request);
  const response = await transport({
    url: "https://api.x.ai/v1/responses",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(body),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeGrokResponsesResponse(JSON.parse(responseBody));
}

// src/client/extensions/grok/grok-code-execution.ts
function grokCodeExecutionRequest(init) {
  return {
    model: init.model ?? "grok-4-1-fast",
    input: init.input,
    ...init.stream !== void 0 && { stream: init.stream }
  };
}
async function grokCodeExecution(options) {
  const tool = { type: "code_interpreter" };
  const responsesRequest = {
    model: options.request.model,
    input: options.request.input,
    tools: [tool],
    ...options.request.stream !== void 0 && { stream: options.request.stream }
  };
  return grokResponses({
    request: responsesRequest,
    apiKey: options.apiKey,
    ...options.transport !== void 0 && { transport: options.transport },
    ...options.signal !== void 0 && { signal: options.signal }
  });
}

// src/client/extensions/grok/grok-search-tools.ts
function grokSearchToolsRequest(init) {
  return {
    model: init.model ?? "grok-4-1-fast",
    input: init.input,
    tools: init.tools,
    ...init.stream !== void 0 && { stream: init.stream }
  };
}
function grokWebSearchRequest(init) {
  const tool = {
    type: "web_search",
    ...init.options !== void 0 && { webSearch: init.options }
  };
  return grokSearchToolsRequest({
    ...init.model !== void 0 && { model: init.model },
    input: init.input,
    tools: [tool],
    ...init.stream !== void 0 && { stream: init.stream }
  });
}
function grokXSearchRequest(init) {
  const tool = {
    type: "x_search",
    ...init.options !== void 0 && { xSearch: init.options }
  };
  return grokSearchToolsRequest({
    ...init.model !== void 0 && { model: init.model },
    input: init.input,
    tools: [tool],
    ...init.stream !== void 0 && { stream: init.stream }
  });
}
async function grokSearchTools(options) {
  const responsesRequest = {
    model: options.request.model,
    input: options.request.input,
    tools: options.request.tools,
    ...options.request.stream !== void 0 && { stream: options.request.stream }
  };
  return grokResponses({
    request: responsesRequest,
    apiKey: options.apiKey,
    ...options.transport !== void 0 && { transport: options.transport },
    ...options.signal !== void 0 && { signal: options.signal }
  });
}

// src/client/extensions/grok/grok-image-generation.ts
function grokImageGenerationRequest(init) {
  return {
    model: init.model ?? "grok-2-image",
    prompt: init.prompt,
    ...init.n !== void 0 && { n: init.n },
    ...init.responseFormat !== void 0 && { responseFormat: init.responseFormat }
  };
}
function encodeGrokImageGenerationRequest(request) {
  const out = { model: request.model, prompt: request.prompt };
  if (request.n !== void 0) out["n"] = request.n;
  if (request.responseFormat !== void 0) out["response_format"] = request.responseFormat;
  return out;
}
function asRecord4(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
}
function asString3(value) {
  return typeof value === "string" ? value : void 0;
}
function asNumber3(value) {
  return typeof value === "number" ? value : void 0;
}
function decodeResult(value) {
  const record = asRecord4(value) ?? {};
  return {
    url: asString3(record["url"]),
    b64Json: asString3(record["b64_json"]),
    revisedPrompt: asString3(record["revised_prompt"])
  };
}
function decodeGrokImageGenerationResponse(value) {
  const record = asRecord4(value) ?? {};
  const data = record["data"];
  return {
    created: asNumber3(record["created"]),
    data: Array.isArray(data) ? data.map(decodeResult) : []
  };
}
async function grokImageGeneration(options) {
  const transport = options.transport ?? defaultTransport;
  const body = encodeGrokImageGenerationRequest(options.request);
  const response = await transport({
    url: "https://api.x.ai/v1/images/generations",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(body),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeGrokImageGenerationResponse(JSON.parse(responseBody));
}

// src/client/extensions/grok/grok-image-understanding.ts
async function grokImageUnderstanding(options) {
  return options.client.send({
    messages: options.messages,
    provider: AIProvider.xai,
    model: options.model,
    apiKey: options.apiKey,
    system: options.system,
    maxTokens: options.maxTokens ?? 4096,
    settings: options.settings,
    signal: options.signal
  });
}

// src/client/extensions/zai/zai-cogvideo.ts
async function zaiCogVideo(options) {
  const transport = options.transport ?? defaultTransport;
  const response = await transport({
    url: "https://api.z.ai/api/paas/v4/videos/generations",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(encodeCogVideoRequest(options.request)),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  const json = parseObject(responseBody, "CogVideo");
  return {
    id: str(json["id"]),
    requestId: str(json["request_id"]),
    created: num(json["created"]),
    status: str(json["status"])
  };
}
async function zaiCogVideoResult(options) {
  const transport = options.transport ?? defaultTransport;
  const response = await transport({
    url: `https://api.z.ai/api/paas/v4/videos/result/${options.id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeCogVideoResult(responseBody);
}
async function zaiCogVideoWaitForResult(options) {
  const pollIntervalMs = options.pollIntervalMs ?? 2e3;
  const maxAttempts = options.maxAttempts ?? 60;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options.signal?.aborted) {
      throw APIError.cancelled();
    }
    const result = await zaiCogVideoResult({
      id: options.id,
      apiKey: options.apiKey,
      transport: options.transport,
      signal: options.signal
    });
    if (result.status !== "PROCESSING") {
      return result;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, pollIntervalMs));
    }
  }
  throw APIError.networkError(
    `Timed out waiting for CogVideo result ${options.id} after ${maxAttempts} attempts`
  );
}
function encodeCogVideoRequest(request) {
  const body = {
    model: request.model ?? "cogvideox-3",
    prompt: request.prompt
  };
  if (request.imageUrl !== void 0) {
    body["image_url"] = Array.isArray(request.imageUrl) ? [...request.imageUrl] : request.imageUrl;
  }
  if (request.quality !== void 0) body["quality"] = request.quality;
  if (request.withAudio !== void 0) body["with_audio"] = request.withAudio;
  if (request.size !== void 0) body["size"] = request.size;
  if (request.fps !== void 0) body["fps"] = request.fps;
  return body;
}
function decodeCogVideoResult(responseBody) {
  const json = parseObject(responseBody, "CogVideo result");
  let data;
  if (Array.isArray(json["data"])) {
    data = json["data"].map((entry) => {
      const record = isRecord(entry) ? entry : {};
      return {
        url: str(record["url"]),
        cover: str(record["cover"]),
        duration: num(record["duration"])
      };
    });
  }
  return {
    id: str(json["id"]),
    status: str(json["status"]),
    videoUrl: str(json["video_url"]),
    coverUrl: str(json["cover_url"]),
    data
  };
}
function parseObject(body, label) {
  let json;
  try {
    json = JSON.parse(body);
  } catch {
    throw APIError.parseError(`Invalid ${label} response`);
  }
  if (!isRecord(json)) {
    throw APIError.parseError(`Invalid ${label} response`);
  }
  return json;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function str(value) {
  return typeof value === "string" ? value : void 0;
}
function num(value) {
  return typeof value === "number" ? value : void 0;
}

// src/client/extensions/zai/zai-cogview.ts
async function zaiCogView(options) {
  const transport = options.transport ?? defaultTransport;
  const body = {
    model: options.request.model ?? "cogView-4-250304",
    prompt: options.request.prompt
  };
  if (options.request.size !== void 0) body["size"] = options.request.size;
  const response = await transport({
    url: "https://api.z.ai/api/paas/v4/images/generations",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(body),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeCogViewResponse(responseBody);
}
function decodeCogViewResponse(responseBody) {
  let json;
  try {
    json = JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid CogView response");
  }
  if (!isRecord2(json) || !Array.isArray(json["data"])) {
    throw APIError.parseError("Invalid CogView response");
  }
  const data = json["data"].map((entry) => {
    const record = isRecord2(entry) ? entry : {};
    return {
      url: typeof record["url"] === "string" ? record["url"] : void 0,
      b64Json: typeof record["b64_json"] === "string" ? record["b64_json"] : void 0
    };
  });
  return {
    created: typeof json["created"] === "number" ? json["created"] : void 0,
    data
  };
}
function isRecord2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/client/extensions/zai/zai-websearch.ts
async function zaiWebSearch(options) {
  const transport = options.transport ?? defaultTransport;
  const response = await transport({
    url: "https://api.z.ai/api/paas/v4/tools/web-search",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(encodeWebSearchRequest(options.request)),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  const json = parseObject2(responseBody, "web search");
  if (!Array.isArray(json["search_result"])) {
    throw APIError.parseError("Invalid web search response");
  }
  return {
    created: num2(json["created"]),
    id: str2(json["id"]),
    requestId: str2(json["request_id"]),
    searchResult: json["search_result"].map(decodeSearchResult)
  };
}
async function zaiWebSearchChat(options) {
  const transport = options.transport ?? defaultTransport;
  const response = await transport({
    url: "https://api.z.ai/api/paas/v4/chat/completions",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${options.apiKey}`
    },
    body: JSON.stringify(encodeWebSearchChatRequest(options.request)),
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeWebSearchChatResponse(responseBody);
}
function encodeWebSearchRequest(request) {
  const body = {
    search_engine: request.searchEngine,
    search_query: request.searchQuery
  };
  if (request.count !== void 0) body["count"] = request.count;
  if (request.searchDomainFilter !== void 0) {
    body["search_domain_filter"] = request.searchDomainFilter;
  }
  if (request.searchRecencyFilter !== void 0) {
    body["search_recency_filter"] = request.searchRecencyFilter;
  }
  if (request.contentSize !== void 0) body["content_size"] = request.contentSize;
  return body;
}
function encodeWebSearchChatRequest(request) {
  return {
    model: request.model,
    messages: request.messages.map((message) => ({
      role: message.role,
      content: message.content
    })),
    tools: request.tools.map((tool) => {
      const encoded = { type: tool.type };
      if (tool.webSearch !== void 0) {
        encoded["web_search"] = encodeWebSearchToolConfig(tool.webSearch);
      }
      return encoded;
    })
  };
}
function encodeWebSearchToolConfig(config) {
  const encoded = {};
  if (config.enable !== void 0) encoded["enable"] = config.enable;
  if (config.searchEngine !== void 0) encoded["search_engine"] = config.searchEngine;
  if (config.searchResult !== void 0) encoded["search_result"] = config.searchResult;
  if (config.searchPrompt !== void 0) encoded["search_prompt"] = config.searchPrompt;
  if (config.count !== void 0) encoded["count"] = config.count;
  if (config.searchDomainFilter !== void 0) {
    encoded["search_domain_filter"] = config.searchDomainFilter;
  }
  if (config.searchRecencyFilter !== void 0) {
    encoded["search_recency_filter"] = config.searchRecencyFilter;
  }
  if (config.contentSize !== void 0) encoded["content_size"] = config.contentSize;
  return encoded;
}
function decodeSearchResult(entry) {
  const record = isRecord3(entry) ? entry : {};
  return {
    title: str2(record["title"]),
    link: str2(record["link"]),
    content: str2(record["content"]),
    media: str2(record["media"]),
    icon: str2(record["icon"]),
    publishDate: str2(record["publish_date"]),
    refer: str2(record["refer"])
  };
}
function decodeWebSearchChatResponse(responseBody) {
  const json = parseObject2(responseBody, "web search chat");
  let choices;
  if (Array.isArray(json["choices"])) {
    choices = json["choices"].map((entry) => {
      const record = isRecord3(entry) ? entry : {};
      const message = isRecord3(record["message"]) ? {
        role: str2(record["message"]["role"]),
        content: str2(record["message"]["content"])
      } : void 0;
      return {
        index: num2(record["index"]),
        message,
        finishReason: str2(record["finish_reason"])
      };
    });
  }
  return {
    id: str2(json["id"]),
    created: num2(json["created"]),
    model: str2(json["model"]),
    choices,
    webSearch: Array.isArray(json["web_search"]) ? json["web_search"].map(decodeSearchResult) : void 0
  };
}
function parseObject2(body, label) {
  let json;
  try {
    json = JSON.parse(body);
  } catch {
    throw APIError.parseError(`Invalid ${label} response`);
  }
  if (!isRecord3(json)) {
    throw APIError.parseError(`Invalid ${label} response`);
  }
  return json;
}
function isRecord3(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function str2(value) {
  return typeof value === "string" ? value : void 0;
}
function num2(value) {
  return typeof value === "number" ? value : void 0;
}

// src/client/extensions/zai/zai-glmasr.ts
async function zaiGLMASR(options) {
  const transport = options.transport ?? defaultTransport;
  const { request } = options;
  const form = new FormData();
  form.append("model", request.model ?? "glm-asr-2512");
  form.append("stream", request.stream === true ? "true" : "false");
  form.append(
    "file",
    new Blob([request.fileData], { type: request.mimeType }),
    request.fileName
  );
  const response = await transport({
    url: "https://api.z.ai/api/paas/v4/audio/transcriptions",
    method: "POST",
    // No Content-Type header: the runtime sets `multipart/form-data` with the
    // generated boundary when the body is a FormData.
    headers: {
      Authorization: `Bearer ${options.apiKey}`
    },
    body: form,
    signal: options.signal
  });
  const responseBody = await response.text();
  if (response.status !== 200) {
    throw ErrorResponseParser.makeError(response.status, responseBody);
  }
  return decodeGLMASRResponse(responseBody);
}
function decodeGLMASRResponse(responseBody) {
  let json;
  try {
    json = JSON.parse(responseBody);
  } catch {
    throw APIError.parseError("Invalid GLM-ASR response");
  }
  if (!isRecord4(json)) {
    throw APIError.parseError("Invalid GLM-ASR response");
  }
  let segments;
  if (Array.isArray(json["segments"])) {
    segments = json["segments"].map((entry) => {
      const record = isRecord4(entry) ? entry : {};
      return {
        start: typeof record["start"] === "number" ? record["start"] : void 0,
        end: typeof record["end"] === "number" ? record["end"] : void 0,
        text: typeof record["text"] === "string" ? record["text"] : void 0
      };
    });
  }
  return {
    text: typeof json["text"] === "string" ? json["text"] : void 0,
    requestId: typeof json["request_id"] === "string" ? json["request_id"] : void 0,
    segments
  };
}
function isRecord4(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export { ALL_OAUTH_PROVIDERS, AnthropicContentReplay, AnthropicContentReplayAccumulator, GEMINI_GROUNDING_GOOGLE_SEARCH_TOOL, GEMINI_THOUGHT_SIGNATURE_FALLBACK, GeminiLiveAPIClient, GeminiLiveModel, InMemoryCredentialStore, OAuthError, OAuthManager, OAuthProvider, OPENAI_CODEX_CALLBACK_SCHEME, OPENAI_CODEX_CALLBACK_URL, OPENAI_CODEX_PROVIDER, OpenAIAgenticResponsesClient, OpenAIAudioTranscriptionClient, OpenAIChatResponses, OpenAICodexOAuth, OpenAIComputerUseClient, OpenAIContentFormatter, OpenAIDeepResearchClient, OpenAIEmbeddingsClient, OpenAIImageGenerationClient, OpenAIResponsesStreamReducer, OpenAITTSClient, OpenAITTSVoice, OpenAIVideoGenerationClient, OpenAIWebSearchClient, ProviderClient, ProviderRequestSupport, SSELineBuffer, SSEParser, StreamedJSONObjectAccumulator, StreamingClient, ToolFormatters, anthropicContentBlocks, anthropicTokenUsage, asArray, asBoolean, asNumber, asRecord, asString, asStringArray, buildAnthropicMessages, buildGeminiContents, buildOpenAICompatibleMessages, combinedText, computeCodeChallenge, credentialFromJSON, credentialFromTokenResponse, credentialIsExpired, credentialNeedsRefresh, credentialToJSON, decodeComputerAction, decodeGeminiLiveServerEvent, decodeGrokImageGenerationResponse, decodeGrokResponsesResponse, decodeResponsesAction, decodeResponsesAnnotation, decodeResponsesCitation, decodeResponsesFailure, decodeResponsesOutput, decodeResponsesOutputContent, decodeResponsesResponse, decodeResponsesStreamEvent, decodeResponsesToolResult, decodeSafetyCheck, defaultTransport, encodeGeminiLiveClientMessage, encodeGrokImageGenerationRequest, encodeGrokResponsesRequest, encodeGrokToolDescriptor, encodeGrokWebSearchOptions, encodeGrokXSearchOptions, extractJsonObject, geminiAudioUnderstanding, geminiAudioUnderstandingText, geminiFunctionCallPart, geminiFunctionResponsePart, geminiGroundingInlineCitations, geminiGroundingText, geminiGroundingWithGoogleSearch, geminiImageGeneration, geminiImageGenerationImages, geminiLiveEventsFrom, geminiParts, geminiThinking, geminiThinkingOutput, generateCodeVerifier, grokCodeExecution, grokCodeExecutionRequest, grokImageGeneration, grokImageGenerationRequest, grokImageUnderstanding, grokResponses, grokResponsesCombinedText, grokSearchTools, grokSearchToolsRequest, grokWebSearchRequest, grokXSearchRequest, oauthProviderDescription, oauthProviderDisplayName, parseEmailFromIdToken, sendWithOAuth, serializeApproximateLocation, serializeResponsesContent, serializeResponsesInput, serializeResponsesInputItem, serializeResponsesOutputPayload, serializeResponsesReasoning, serializeResponsesRequest, serializeResponsesTool, serializeSafetyCheck, serializeUserLocation, serializeWebSearchFilters, streamWithOAuth, verifyOpenAIWebhookSignature, zaiCogVideo, zaiCogVideoResult, zaiCogVideoWaitForResult, zaiCogView, zaiGLMASR, zaiWebSearch, zaiWebSearchChat };
//# sourceMappingURL=chunk-EK6C7HYF.js.map
//# sourceMappingURL=chunk-EK6C7HYF.js.map